import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { SelectionModel } from '@angular/cdk/collections';
import { auditTime, filter, Observable, Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FileUploadDialogComponent } from '../../global-components/file-upload-dialog/file-upload-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { MtxTooltipModule } from '@ng-matero/extensions/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ConfigService as AppConfigService } from '../../../../../my-app/src/app/product-health/services/config.service';
import { PopupTableComponent } from '../../global-components/popup-table/popup-table.component';
import { ConfirmDialogueComponent } from '../../../../../my-app/src/app/product-health/components/confirm-dialogue/confirm-dialogue.component';

@Component({
  selector: 'lib-dynamic-table',
  imports: [
    CommonModule,
    MatTableModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    NgxDatatableModule,
    MatTooltipModule,
    MatRadioModule,
    MtxTooltipModule,
    MatMenuModule,
  ],
  templateUrl: './dynamic-table.component.html',
  styleUrl: './dynamic-table.component.scss',
})
export class DynamicTableComponent {
  @Input() rows: any[] = [];
  @Input() isEditMode = false;
  @Input() data!: any;
  selectedFilters: any = []; // default
  @Output() rowsSelected = new EventEmitter<any[]>();
  columnMode: ColumnMode = ColumnMode.force;
  Number = Number;
  // (legacy) selection model — kept to avoid breaking other code
  selection = new SelectionModel<any>(true, []);

  // our own selection tracking for header/row checkboxes
  selectedRows: any[] = [];
  selectAllChecked = false;

  private destroy$ = new Subject<void>();
  public hasError = false;
  public errorMessage = '';
  filters: any;
  totalBudget = 0;
  isLoading = true;
  fullData: any;
  selectedFiles: File[] = [];
  note: string = '';
  changed = false;
  originalRows: any[] = [];
  showInvalidOnly = false;
  columnFilters: any = {};
  dropdownSearchText: Record<string, string> = {};
  private displayedColumnsCache: { key: string; value: string[] } | null = null;
  private renderedColumnsCache: { key: string; value: any[] } | null = null;
  private dropdownDisplayCache: Record<string, any[]> = {};
  private dropdownVisibilityCache: Record<string, boolean> = {};
  private headerClassCache: Record<string, string> = {};
  private cellClassCache: Record<string, string> = {};
  private rowHeightCache = new WeakMap<object, number>();
  private cacheReloadTimer: ReturnType<typeof setInterval> | null = null;
  private readonly cacheReloadMs = 10 * 60 * 1000;
  private editAccessRefreshInFlight = false;
  planningYearOptions: number[] = [];
  planningYearLoading = false;
  planningYearError = '';
  hoveredAopCell = '';
  activeCommentEditorRow: any = null;
  private activeCommentEditorFocusLocked = false;
  private planningYearRequestStarted = false;
  email: string = '';
  private currentUserRole = '';
  private configuredEditMode = false;
  private roleCanEdit = false;
  private latestEditAccessResponse: any = null;
  pinnedColumnProp: string | null = null;
  private sortState: { prop: string | null; dir: 'asc' | 'desc' | null } = {
    prop: null,
    dir: null,
  };
  rowIdentity = (row: any): unknown => row?.uuid ?? row;
  readonly tableRowHeight = (row?: any): number => this.getTableRowHeight(row);
  readonly auditHistoryIcon =
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231f1e1e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='9'/><polyline points='12 7 12 12 16 14'/></svg>";
  private readonly projectScoreProps = new Set([
    'ideation_final_score',
    'simulation_final_score',
    'aop_final_score',
    'final_score',
  ]);

  getDefaultColumnWidth(): number {
    const configuredWidth = Number(this.data?.defaultColumnWidth);
    return Number.isFinite(configuredWidth) && configuredWidth > 0
      ? configuredWidth
      : 200;
  }

  get rowActions(): any[] {
    return Array.isArray(this.data?.rowActions) ? this.data.rowActions : [];
  }

  shouldShowRowActions(): boolean {
    return this.isEditMode && this.rowActions.length > 0;
  }

  shouldShowTopSaveButton(): boolean {
    return this.data?.showTopSaveButton !== false;
  }

  shouldShowAuditHistoryColumn(): boolean {
    return this.isEditMode && this.data?.showAuditHistory !== false;
  }

  shouldShowRowSaveColumn(): boolean {
    return this.isEditMode && this.data?.rowSaveButton === true;
  }

  getRowSaveColumnWidth(): number {
    const configuredWidth = Number(this.data?.rowSaveColumnWidth);
    return Number.isFinite(configuredWidth) && configuredWidth > 0
      ? configuredWidth
      : 120;
  }

  getRowSaveButtonWidth(): number {
    const configuredWidth = Number(this.data?.rowSaveButtonWidth);
    return Number.isFinite(configuredWidth) && configuredWidth > 0
      ? configuredWidth
      : 64;
  }

  getRowSaveButtonLabel(): string {
    return String(this.data?.rowSaveButtonLabel || 'Save');
  }

  getRowSaveColumnLabel(): string {
    return String(
      this.data?.rowSaveColumnLabel ?? this.getRowSaveButtonLabel()
    );
  }

  isRowSaveDisabled(row: any, rowIndex?: number): boolean {
    if (!this.shouldDisableRowSaveWhenFrozen() || !this.isRowEditFrozen(row, rowIndex)) {
      return false;
    }

    const allowWhen = this.data?.rowSaveWhenFrozenWhen;
    return allowWhen ? !this.doesRowMatchRule(row, allowWhen) : true;
  }

  getRowSaveTooltip(row: any, rowIndex?: number): string {
    return this.isRowSaveDisabled(row, rowIndex)
      ? 'Published rows cannot be saved'
      : `${this.getRowSaveButtonLabel()} row`;
  }

  getUnsavedNote(): string {
    return String(
      this.data?.unsavedNote || 'Record is not saved until save button is clicked'
    );
  }

  shouldShowTrailingDeleteColumn(): boolean {
    return (
      this.isEditMode &&
      this.data?.buttons?.includes('delete') &&
      this.data?.deleteColumnAtEnd === true
    );
  }

  getRowActionColumnWidth(): number {
    const configuredWidth = Number(this.data?.rowActionColumnWidth);
    return Number.isFinite(configuredWidth) && configuredWidth > 0
      ? configuredWidth
      : 340;
  }

  getRowActionColumnLabel(): string {
    return String(this.data?.rowActionColumnLabel ?? 'Options');
  }

  getRowActionButtonStyle(): string {
    const width = Number(this.data?.rowActionButtonWidth);
    const widthStyle = Number.isFinite(width) && width > 0
      ? `width: ${width}px;`
      : '';

    return `${widthStyle} padding: 2px 8px; min-width: 0; height: 30px; line-height: 18px; font-size: 14px; white-space: nowrap;`;
  }

  isDeleteDisabled(row: any, rowIndex?: number): boolean {
    return (
      row?.is_deleted ||
      (this.isRowEditFrozen(row, rowIndex) && !this.allowDeleteWhenFrozen())
    );
  }

  getDeleteTooltip(row: any, rowIndex?: number): string {
    if (row?.is_deleted) {
      return 'Row marked for deletion';
    }

    if (this.isRowEditFrozen(row, rowIndex) && !this.allowDeleteWhenFrozen()) {
      return 'Row is locked for editing';
    }

    return 'Delete row';
  }

  private allowDeleteWhenFrozen(): boolean {
    return [this.data?.allowDeleteWhenFrozen, this.data?.deleteAction?.allowWhenFrozen]
      .some(value => String(value).trim().toLowerCase() === 'true');
  }

  private shouldDisableRowSaveWhenFrozen(): boolean {
    return String(this.data?.disableRowSaveWhenFrozen).trim().toLowerCase() === 'true';
  }

  isRowActionDisabled(action: any, row: any): boolean {
    const enabledWhen = action?.enabledWhen;
    if (enabledWhen && !this.doesRowMatchRule(row, enabledWhen)) {
      return true;
    }

    const when = action?.disabledWhen;
    if (!when) {
      return false;
    }
    return this.doesRowMatchRule(row, when);
  }

  onRowAction(action: any, row: any, rowIndex: number): void {
    if (this.isRowActionDisabled(action, row)) {
      return;
    }

    if (action?.confirmBeforeApi) {
      const dialogRef = this.dialog.open(ConfirmDialogueComponent, {
        width: action.confirmWidth || '420px',
        disableClose: true,
        data: {
          title: action.confirmTitle || `Confirm ${action?.label || 'Action'}`,
          desc:
            action.confirmMessage ||
            `Do you really want to ${String(action?.label || 'continue').toLowerCase()}?`,
          noLabel: action.cancelLabel || 'No',
          yesLabel: action.okLabel || 'Yes',
          showContinueText: false,
          showCloseButton: true,
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.executeRowAction(action, row, rowIndex);
        }
      });
      return;
    }

    this.executeRowAction(action, row, rowIndex);
  }

  private executeRowAction(action: any, row: any, rowIndex: number): void {
    if (action?.api?.payload) {
      this.runRowActionApi(action, row, rowIndex);
      return;
    }

    const statusValue = action?.setStatus;
    if (statusValue !== undefined) {
      const statusProp = action?.statusProp || 'status';
      this.rows[rowIndex][statusProp] = statusValue;
      if (this.rows[rowIndex].row_status !== 'new') {
        this.rows[rowIndex].row_status = 'edited';
      }
      this.note = this.getUnsavedNote();
      this.changed = true;
    }

    const message = action?.message || `${action?.label || 'Action'} applied.`;
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  private runRowActionApi(action: any, row: any, rowIndex: number): void {
    this.isLoading = true;
    const payload = this.replacePayloadTokens(
      action.api.payload,
      this.buildPayloadTokenReplacements(row)
    );

    this.configService
      .customApi(
        action.api.url || this.data?.saveUrl || this.data?.apiUrl,
        payload,
        this.data,
        action.api.method || 'post'
      )
      .subscribe({
        next: (response: any) => {
          const commitStatus = this.getCommitStatus(response);
          if (commitStatus && commitStatus !== 'COMMIT_SUCCESS') {
            this.isLoading = false;
            this.snackBar.open(
              this.getErrorStackMessage(response) ||
                `${action.label || 'Action'} failed: Commit Status ${commitStatus}`,
              'Close',
              {
                duration: 5000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['snackbar-error'],
              }
            );
            return;
          }

          this.runPostSaveActions(row, (postSaveResponses) => {
            const refreshResponse = postSaveResponses?.[postSaveResponses.length - 1];
            if (refreshResponse) {
              this.applyRowsResponse(refreshResponse);
            } else {
              this.rows[rowIndex].row_status = 'unchanged';
              this.originalRows = this.cloneRows(this.rows);
            }

            this.isLoading = false;
            if (action?.successDialog) {
              this.showRowActionSuccessDialog(action);
              return;
            }

            this.snackBar.open(
              action.successMessage || `${action.label || 'Action'} completed successfully!`,
              'Close',
              {
                duration: 5000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['snackbar-success'],
              }
            );
          });
        },
        error: (err: any) => {
          console.error('Row action failed:', err);
          this.isLoading = false;
          this.snackBar.open(
            this.getErrorStackMessage(err?.error || err) ||
              action.errorMessage ||
              `${action.label || 'Action'} failed.`,
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            }
          );
        },
      });
  }

  private showRowActionSuccessDialog(action: any): void {
    const successDialog = action.successDialog || {};
    this.dialog.open(ConfirmDialogueComponent, {
      width: successDialog.width || action.confirmWidth || '420px',
      data: {
        title: successDialog.title || `${action?.label || 'Action'} Complete`,
        desc:
          successDialog.message ||
          action.successMessage ||
          `${action?.label || 'Action'} completed successfully!`,
        yesLabel: successDialog.okLabel || 'OK',
        hideNoButton: true,
        showContinueText: false,
        showCloseButton: true,
      },
    });
  }

  isCellEditableForRow(column: any, row: any, rowIndex?: number): boolean {
    if (!this.isEditMode || row?.is_deleted) return false;
    if (this.isCellEditFrozen(column, row, rowIndex)) return false;
    if (this.isColumnLockedByEditRule(column, row)) return false;
    if (!this.isColumnEditableForRow(column, row)) return false;
    if (column?.editable) return true;
    return !!(column?.editableOnNewRowOnly && row?.row_status === 'new');
  }

  private isColumnLockedByEditRule(column: any, row: any): boolean {
    const rowStatus = String(row?.row_status || '');
    if (
      ['new', 'deleted'].includes(rowStatus) ||
      (rowStatus === 'edited' && this.data?.preserveFreezeWhenEdited !== true)
    ) {
      return false;
    }

    const rules = Array.isArray(this.data?.editLockWhen)
      ? this.data.editLockWhen
      : this.data?.editLockWhen
        ? [this.data.editLockWhen]
        : [];

    return rules.some((rule: any) => {
      if (!['dateEqualsToday', 'dateOnOrBeforeToday'].includes(rule?.type)) {
        return false;
      }

      const dateProp = rule.field || rule.prop;
      const rowDate = this.parseDateOnly(row?.[dateProp]);
      const today = this.toDateOnly(new Date());
      const isMatch =
        !!rowDate &&
        (rule.type === 'dateOnOrBeforeToday'
          ? rowDate.getTime() <= today.getTime()
          : rowDate.getTime() === today.getTime());
      if (!isMatch) {
        return false;
      }

      const editableProps = Array.isArray(rule.editableProps)
        ? rule.editableProps
        : [];
      return !editableProps.includes(column?.prop);
    });
  }

  private isCellEditFrozen(column: any, row: any, rowIndex?: number): boolean {
    if (!this.isRowEditFrozen(row, rowIndex)) {
      return false;
    }

    if (column?.allowEditWhenFrozen !== true) {
      return true;
    }

    const allowWhen = column?.allowEditWhenFrozenWhen;
    return allowWhen ? !this.doesRowMatchRule(row, allowWhen) : false;
  }

  isColumnEditableForRow(column: any, row: any): boolean {
    if (!column?.editableWhen) return true;
    return this.doesRowMatchRule(row, column.editableWhen);
  }

  isRowEditFrozen(row: any, rowIndex?: number): boolean {
    const rule = this.getFreezeEditRule(row);
    if (!rule || !row || typeof row !== 'object') return false;

    const field = String(rule.field || rule.prop || '').trim();
    if (!field) return false;

    if (['new', 'edited', 'deleted'].includes(String(row?.row_status || ''))) {
      return false;
    }

    const savedRow = this.findSavedRowSnapshot(row, rowIndex);
    if (!savedRow && field === 'pm_approval') {
      return false;
    }

    const rowValue = this.normalizeComparableValue(
      savedRow ? savedRow[field] : row[field]
    );
    const matches = Array.isArray(rule.equals)
      ? rule.equals
      : Array.isArray(rule.values)
        ? rule.values
        : rule.value !== undefined
          ? [rule.value]
          : [];

    return matches
      .map((value: any) => this.normalizeComparableValue(value))
      .includes(rowValue);
  }

  private getFreezeEditRule(row: any): any | null {
    if (this.data?.freezeEditWhen) {
      if (!this.canApplyFreezeEditRule()) {
        return null;
      }
      return this.data.freezeEditWhen;
    }

    return this.hasPmApprovalField(row) && this.canApplyFreezeEditRule()
      ? { field: 'pm_approval', equals: ['Yes'] }
      : null;
  }

  private canApplyFreezeEditRule(): boolean {
    if (Array.isArray(this.data?.freezeEditExceptRoles)) {
      return !this.data.freezeEditExceptRoles.includes(this.currentUserRole);
    }

    const freezeRoles = Array.isArray(this.data?.freezeEditRoles)
      ? this.data.freezeEditRoles
      : null;

    return freezeRoles ? freezeRoles.includes(this.currentUserRole) : true;
  }

  private hasPmApprovalField(row: any): boolean {
    if (row && Object.prototype.hasOwnProperty.call(row, 'pm_approval')) {
      return true;
    }

    const headers = Array.isArray(this.data?.headers) ? this.data.headers : [];
    if (headers.some((header: any) => header?.prop === 'pm_approval')) {
      return true;
    }

    const attributes = Array.isArray(this.data?.attributes)
      ? this.data.attributes
      : [];
    return attributes.includes('pm_approval');
  }

  private findSavedRowSnapshot(row: any, rowIndex?: number): any | null {
    if (!Array.isArray(this.originalRows) || !row || typeof row !== 'object') {
      return null;
    }

    const uuid = this.normalizeComparableValue(row.uuid);
    if (uuid) {
      const match = this.originalRows.find(
        (originalRow: any) => this.normalizeComparableValue(originalRow?.uuid) === uuid
      );
      if (match) return match;
    }

    const projectNumber = this.normalizeComparableValue(
      row.vpcr_number ?? row.project_number
    );
    if (projectNumber) {
      const match = this.originalRows.find(
        (originalRow: any) =>
          this.normalizeComparableValue(
            originalRow?.vpcr_number ?? originalRow?.project_number
          ) === projectNumber
      );
      if (match) return match;
    }

    if (
      Number.isInteger(rowIndex) &&
      rowIndex! >= 0 &&
      rowIndex! < this.originalRows.length
    ) {
      return this.originalRows[rowIndex!];
    }

    return null;
  }

  private normalizeComparableValue(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  constructor(
    private configService: ConfigService,
    private appConfigService: AppConfigService,
    private filterState: GlobalService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private keycloakService: KeycloakService,
    private cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit() {
    this.filterState.filters$
      .pipe(
        // short delay to collect rapid emissions
        auditTime(1000),
        filter((filters) => filters !== null && filters !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe((filters) => {
        this.filters = { ...filters.filter };
        this.columnFilters = {};
        this.clearUiCaches();
        this.loadAndRenderChart(filters.filter);
      });
  }

  ngOnInit() {
    this.configuredEditMode = this.data?.editMode === true;
    if (this.hasResponseControlledEditAccess()) {
      this.data.editMode = false;
      this.applyCachedEditAccessState();
    }
    this.startCacheReloadTimer();
    this.checkEditRoleAccess();
    this.loadColumnDropdownOptions();
    if (this.shouldUseLocalRows()) {
      this.loadAndRenderChart(this.filters || {});
    }
  }

  private loadColumnDropdownOptions(): void {
    const dropdownColumns = (this.data?.headers || []).filter(
      (column: any) =>
        column?.type === 'dropdown' &&
        (
          column?.optionsSource === 'attributeValidValues' ||
          (column?.optionsApiUrl && column?.optionsFunctionName)
        )
    );

    dropdownColumns.forEach((column: any) => {
      if (column?.optionsSource === 'attributeValidValues') {
        const payload =
          column.optionsMetadataPayload || this.data?.optionsMetadataPayload || {
            entityName: column.optionsEntityName || this.data?.optionsEntityName || this.data?.savePayloadConfig?.entity,
            attributesList: column.optionsAttributesList || ['*'],
            filterList: column.optionsFilterList || [],
          };

        this.configService
          .getEntityStoreMetadata(column.optionsMetadataApiUrl || this.data?.optionsMetadataApiUrl || '/spriced/platform/getData', payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              const options = this.extractDropdownOptions(res, column);

              column.list = Array.isArray(options) ? options : [];
              this.dropdownDisplayCache = {};
              this.dropdownVisibilityCache = {};
              this.cdr.detectChanges();
            },
            error: (error) => {
              console.error(`Failed to load dropdown options for ${column.prop}`, error);
            },
          });
        return;
      }

      this.configService
        .fetchData(
          column.optionsApiUrl,
          column.optionsFunctionName,
          this.filters || {},
          column
        )
        .subscribe({
          next: (res: any) => {
            const options = this.extractDropdownOptions(res, column);

            column.list = Array.isArray(options) ? options : [];
            this.dropdownDisplayCache = {};
            this.dropdownVisibilityCache = {};
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error(`Failed to load dropdown options for ${column.prop}`, error);
          },
        });
    });
  }

  private extractDropdownOptions(response: any, column: any): any[] {
    const responseData = response?.responseList?.[0]?.responseData;
    const primaryPayload = Array.isArray(responseData) ? responseData[0] : responseData;

    if (column?.optionsSource === 'attributeValidValues') {
      const attributeName = column.optionsAttributeName || column.prop;
      const metadataRoot = column.optionsMetadataEntityName
        ? this.findEntityMetadata(response, column.optionsMetadataEntityName) ?? response
        : response;
      const attribute = this.findAttributeMetadata(metadataRoot, attributeName);
      return Array.isArray(attribute?.validValues) ? attribute.validValues : [];
    }

    if (column?.optionsRespKey) {
      return (
        primaryPayload?.[column.optionsRespKey] ??
        response?.responseList?.[0]?.[column.optionsRespKey] ??
        response?.[column.optionsRespKey] ??
        []
      );
    }

    return primaryPayload ?? response;
  }

  private startCacheReloadTimer(): void {
    this.stopCacheReloadTimer();
    this.cacheReloadTimer = setInterval(() => {
      this.clearUiCaches();
    }, this.cacheReloadMs);
  }

  private stopCacheReloadTimer(): void {
    if (this.cacheReloadTimer !== null) {
      clearInterval(this.cacheReloadTimer);
      this.cacheReloadTimer = null;
    }
  }

  private clearUiCaches(): void {
    this.uniqueValuesCache = {};
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    this.headerClassCache = {};
    this.cellClassCache = {};
    this.rowHeightCache = new WeakMap<object, number>();
    this.displayedColumnsCache = null;
    this.renderedColumnsCache = null;
  }

  checkEditRoleAccess() {
    this.email = this.getCurrentUserEmail();
    this.configService.getRole(this.email).subscribe((role: any) => {
      this.currentUserRole = role || '';
      this.roleCanEdit = this.data.editRoles?.includes(role) && this.configuredEditMode;
      this.clearUiCaches();
      this.applyRoleEditAccess();
    });
  }

  canShowEditAccessAction(): boolean {
    const roles = this.getAlwaysEditRoles();
    if (!roles.length) {
      return true;
    }

    return roles.includes(this.currentUserRole);
  }

  getCurrentEditAccessEnabled(): boolean {
    if (!this.hasResponseControlledEditAccess()) {
      return this.data?.editMode === true;
    }

    return this.readBooleanByKey(
      this.latestEditAccessResponse,
      this.data.editAccessControl?.responseKey || 'is_edit_enabled'
    ) === true;
  }

  shouldShowEditModeTabs(): boolean {
    if (!this.configuredEditMode) {
      return false;
    }

    if (!this.hasResponseControlledEditAccess()) {
      return this.data?.editMode === true;
    }

    return this.latestEditAccessResponse !== null && this.getCurrentEditAccessEnabled();
  }

  getEditAccessIcon(): string {
    return this.getCurrentEditAccessEnabled() ? 'lock_open' : 'lock';
  }

  getEditAccessTooltip(): string {
    const action = this.data?.editAccessAction || {};
    return this.getCurrentEditAccessEnabled()
      ? action.unlockedTooltip || 'Edit Access Unlocked'
      : action.lockedTooltip || action.tooltip || 'Edit Access Locked';
  }

  private applyRoleEditAccess(): void {
    if (!this.hasResponseControlledEditAccess()) {
      this.data.editMode = this.roleCanEdit;
      return;
    }

    this.data.editMode =
      this.roleCanEdit &&
      (this.isAlwaysEditRole() ||
        (this.latestEditAccessResponse !== null &&
          this.readBooleanByKey(
            this.latestEditAccessResponse,
            this.data.editAccessControl?.responseKey || 'is_edit_enabled'
          ) === true));
    this.exitEditIfAccessDisabled();
  }

  private applyResponseEditAccess(response: any): void {
    if (!this.hasResponseControlledEditAccess()) {
      return;
    }

    const editAccessResponse = this.resolveEditAccessResponse(response);
    if (!editAccessResponse) {
      this.applyCachedEditAccessState();
      this.applyRoleEditAccess();
      return;
    }

    this.latestEditAccessResponse = editAccessResponse;
    const responseEditEnabled = this.readBooleanByKey(
      editAccessResponse,
      this.data.editAccessControl?.responseKey || 'is_edit_enabled'
    ) === true;
    this.cacheEditAccessState(responseEditEnabled);

    if (!this.roleCanEdit) {
      this.data.editMode = false;
      this.exitEditIfAccessDisabled();
      return;
    }

    if (this.isAlwaysEditRole()) {
      this.data.editMode = true;
      if (!responseEditEnabled) {
        this.exitEditAfterAccessLocked();
      }
      return;
    }

    this.data.editMode = responseEditEnabled;
    this.exitEditIfAccessDisabled();
  }

  private isEditAccessLockedForCurrentUser(): boolean {
    return (
      this.hasResponseControlledEditAccess() &&
      !this.isAlwaysEditRole() &&
      this.getCurrentEditAccessEnabled() !== true
    );
  }

  private showEditLockedMessage(): void {
    const action = this.data?.editAccessAction || {};
    const message =
      action.lockedSaveMessage ||
      `${action.screenLabel || this.data?.['dashboard-name'] || 'This screen'} is locked by admin. Changes were not saved.`;

    this.snackBar.open(
      message,
      'Close',
      {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error'],
      }
    );
  }

  private refreshEditAccessOnly(
    callback?: (canEdit: boolean) => void,
    showLoading = false
  ): void {
    if (!this.hasResponseControlledEditAccess()) {
      callback?.(this.data?.editMode === true);
      return;
    }

    if (this.editAccessRefreshInFlight) {
      if (showLoading) {
        setTimeout(() => this.refreshEditAccessOnly(callback, showLoading), 100);
        return;
      }
      callback?.(!this.isEditAccessLockedForCurrentUser());
      return;
    }

    this.editAccessRefreshInFlight = true;
    if (showLoading) {
      this.isLoading = true;
    }

    this.configService
      .fetchData(
        this.data.apiUrl,
        this.data.functionName,
        this.filters,
        this.data,
        'post',
        undefined,
        this.email
      )
      .subscribe({
        next: (res) => {
          const primaryPayload = res?.responseList?.[0]?.responseData?.[0];
          this.applyResponseEditAccess(primaryPayload ?? res);
          const canEdit = !this.isEditAccessLockedForCurrentUser();

          if (!canEdit) {
            this.changed = false;
            this.note = '';
            this.rows = this.cloneRows(this.originalRows);
            this.clearUiCaches();
            this.resetSelectionState();
            this.applyFilters();
            this.scheduleTableRecalculate();
          }

          this.editAccessRefreshInFlight = false;
          if (showLoading) {
            this.isLoading = false;
          }
          callback?.(canEdit);
        },
        error: () => {
          this.editAccessRefreshInFlight = false;
          if (showLoading) {
            this.isLoading = false;
          }
          callback?.(!this.isEditAccessLockedForCurrentUser());
        },
      });
  }

  private exitEditIfAccessDisabled(): void {
    if (!this.data.editMode && this.isEditMode) {
      this.exitEdit();
    }
  }

  private exitEditAfterAccessLocked(): void {
    if (!this.isEditMode) {
      return;
    }

    this.isEditMode = false;
    this.changed = false;
    this.note = '';
    this.rows = this.cloneRows(this.originalRows).map((row: any) => ({
      ...row,
      row_status: 'unchanged',
    }));
    this.clearUiCaches();
    this.resetSelectionState();
    this.applyFilters();
    this.scheduleTableRecalculate();
  }

  private hasResponseControlledEditAccess(): boolean {
    return this.data?.editAccessControl?.enabled === true;
  }

  private getAlwaysEditRoles(): string[] {
    return Array.isArray(this.data?.editAccessControl?.alwaysEnabledRoles)
      ? this.data.editAccessControl.alwaysEnabledRoles
      : [];
  }

  private isAlwaysEditRole(): boolean {
    return this.getAlwaysEditRoles().includes(this.currentUserRole);
  }

  private readBooleanByKey(value: any, key: string): boolean | null {
    const foundValue = this.findValueByKey(value, key);
    if (foundValue === true || foundValue === false) {
      return foundValue;
    }

    if (typeof foundValue === 'string') {
      const normalized = foundValue.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }

    return null;
  }

  private resolveEditAccessResponse(response: any): any {
    const action = this.data?.editAccessAction || {};
    const projectCategory = this.resolveEditAccessProjectCategory(action);
    const planningYear = this.resolveEditAccessPlanningYear(action);

    if (!projectCategory && !planningYear) {
      return response;
    }

    return this.findEditAccessRecord(response, projectCategory, planningYear);
  }

  private findEditAccessRecord(value: any, projectCategory: string, planningYear: string): any | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.findEditAccessRecord(item, projectCategory, planningYear);
        if (found) return found;
      }
      return null;
    }

    if (typeof value !== 'object') {
      return null;
    }

    if (this.matchesEditAccessBusinessKey(value, projectCategory, planningYear)) {
      return value;
    }

    for (const item of Object.values(value)) {
      const found = this.findEditAccessRecord(item, projectCategory, planningYear);
      if (found) return found;
    }

    return null;
  }

  private matchesEditAccessBusinessKey(value: any, projectCategory: string, planningYear: string): boolean {
    const responseProjectCategory = this.normalizeBusinessKeyValue(value?.project_category);
    const responsePlanningYear = this.normalizeBusinessKeyValue(value?.[this.getPlanningYearKey()]);
    const expectedProjectCategory = this.normalizeBusinessKeyValue(projectCategory);
    const expectedPlanningYear = this.normalizeBusinessKeyValue(planningYear);

    return (
      (!expectedProjectCategory || responseProjectCategory === expectedProjectCategory) &&
      (!expectedPlanningYear || responsePlanningYear === expectedPlanningYear) &&
      this.findValueByKey(value, this.data.editAccessControl?.responseKey || 'is_edit_enabled') !== undefined
    );
  }

  private applyCachedEditAccessState(): void {
    const cachedValue = this.getCachedEditAccessState();
    const responseKey = this.data?.editAccessControl?.responseKey || 'is_edit_enabled';
    this.latestEditAccessResponse = {
      [responseKey]: cachedValue ?? false,
    };
  }

  private getCachedEditAccessState(): boolean | null {
    const key = this.getEditAccessStorageKey();
    if (!key) return null;

    const value = localStorage.getItem(key);
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  }

  private cacheEditAccessState(isEditEnabled: boolean): void {
    const key = this.getEditAccessStorageKey();
    if (!key) return;
    localStorage.setItem(key, String(isEditEnabled));
  }

  private getEditAccessStorageKey(): string {
    const action = this.data?.editAccessAction || {};
    const projectCategory = this.normalizeBusinessKeyValue(
      this.resolveEditAccessProjectCategory(action)
    );
    const planningYear = this.normalizeBusinessKeyValue(
      this.resolveEditAccessPlanningYear(action)
    );

    if (!projectCategory || !planningYear) {
      return '';
    }

    return `editAccess:${projectCategory}:${planningYear}`;
  }

  private normalizeBusinessKeyValue(value: any): string {
    if (Array.isArray(value)) {
      return this.normalizeBusinessKeyValue(value[0]);
    }

    return String(value ?? '').trim().toLowerCase();
  }

  private getVisibleHeaders(): any[] {
    const headers = Array.isArray(this.data?.headers) ? this.data.headers : [];
    return headers.map((column: any) => this.normalizeRuntimeHeader(column)).filter((column: any) => {
      const visibleRoles = Array.isArray(column?.visibleRoles)
        ? column.visibleRoles
        : [];
      return visibleRoles.length === 0 || visibleRoles.includes(this.currentUserRole);
    });
  }

  private normalizeRuntimeHeader(column: any): any {
    if (column?.prop !== 'user_sales' || this.data?.saveKey !== 'user_revenue') {
      return column;
    }

    return {
      ...column,
      editable: true,
      type: column.type || 'number',
      allowEditWhenFrozen: true,
    };
  }

  private findValueByKey(value: any, key: string): any {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.findValueByKey(item, key);
        if (found !== undefined) return found;
      }
      return undefined;
    }

    if (typeof value === 'object') {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        return value[key];
      }

      for (const item of Object.values(value)) {
        const found = this.findValueByKey(item, key);
        if (found !== undefined) return found;
      }
    }

    return undefined;
  }

  /** Safely parse numbers like "5,200", "$1,234.50", "(1,000)", etc. */
  private parseNumeric(value: any): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;

    let s = String(value).trim();
    if (!s) return 0;

    // Handle accounting negatives like "(1,234.56)"
    const isParenNegative = s.startsWith('(') && s.endsWith(')');

    // Remove commas, currency, and any non digit/.- characters (keep minus & dot)
    s = s.replace(/,/g, '');
    s = s.replace(/[^\d.-]/g, '');

    let n = parseFloat(s);
    if (isNaN(n)) n = 0;

    return isParenNegative ? -Math.abs(n) : n;
  }

  private isErrorPayload(payload: any): boolean {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return false;
    }

    const status = String(payload.status ?? '').toUpperCase();
    const code = Number(payload.code);
    return status === 'ERROR' || (!Number.isNaN(code) && code >= 400);
  }

  private getResponseErrorMessage(primaryPayload: any, res: any): string {
    if (this.isErrorPayload(primaryPayload) && primaryPayload?.message) {
      return String(primaryPayload.message);
    }

    const responseMessage =
      res?.responseList?.[0]?.message ??
      res?.message ??
      res?.error?.message;

    return responseMessage ? String(responseMessage) : '';
  }

  private normalizeMultiSelectValue(value: any): any[] {
    if (value === null || value === undefined || value === '') return [];

    if (Array.isArray(value)) {
      return value
        .flatMap((item) => this.normalizeMultiSelectValue(item))
        .filter((item) => item !== null && item !== undefined && item !== '');
    }

    if (typeof value !== 'string') {
      return [value];
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null') return [];

    const parsedJson = this.tryParseJson(trimmed);
    if (parsedJson !== null) {
      return this.normalizeMultiSelectValue(parsedJson);
    }

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      const inner = trimmed.slice(1, -1).trim();
      if (!inner) return [];

      return inner
        .split(',')
        .map((item) => this.stripOuterQuotes(item.trim()))
        .filter((item) => item.length > 0);
    }

    return [this.stripOuterQuotes(trimmed)];
  }

  private tryParseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private stripOuterQuotes(value: string): string {
    let normalized = value;
    while (
      normalized.length >= 2 &&
      ((normalized.startsWith('"') && normalized.endsWith('"')) ||
        (normalized.startsWith("'") && normalized.endsWith("'")))
    ) {
      normalized = normalized.slice(1, -1).trim();
    }
    return normalized;
  }

  formatCellValue(value: any): string {
    if (Array.isArray(value)) {
      return value
        .filter((item) => item !== null && item !== undefined && item !== '')
        .join(', ');
    }
    if (value === null || value === undefined) return '';
    return String(value);
  }

  getNumberDisplayFormat(prop: string | null | undefined): string {
    const normalizedProp = String(prop || '').toLowerCase();
    if (normalizedProp.includes('investment')) {
      return '1.4-4';
    }
    return this.projectScoreProps.has(String(prop || '')) ? '1.1-1' : '1.0-4';
  }

  getMultiSelectTooltip(value: any): string {
    const selectedValues = this.normalizeMultiSelectValue(value);
    if (!selectedValues.length) return '';

    return selectedValues
      .map((item) => `• ${String(item)}`)
      .join('\n');
  }

  isCommentTooltipColumn(column: any): boolean {
    const prop = String(column?.prop || '').toLowerCase();
    return prop === 'comment' || prop === 'additional_comment';
  }

  isSingleLineCell(column: any, row?: any, rowIndex?: number): boolean {
    if (column?.singleLine !== true) return false;
    return !this.isEditMode || this.isRowEditFrozen(row, rowIndex);
  }

  getCommentTooltip(value: any): string {
    return this.formatCellValue(value).trim();
  }

  hasMandatoryRule(column: any): boolean {
    return column?.mandatory === true || !!column?.mandatoryWhen;
  }

  isColumnMandatoryForRow(column: any, row: any): boolean {
    if (column?.mandatory === true) return true;

    return this.doesRowMatchRule(row, column?.mandatoryWhen);
  }

  private doesRowMatchRule(row: any, rule: any): boolean {
    if (!rule || !row || typeof row !== 'object') return false;

    const field = String(rule.field || rule.prop || '').trim();
    if (!field) return false;

    if (['dateEqualsToday', 'dateOnOrBeforeToday'].includes(rule?.type)) {
      const rowDate = this.parseDateOnly(row[field]);
      const today = this.toDateOnly(new Date());
      return (
        !!rowDate &&
        (rule.type === 'dateOnOrBeforeToday'
          ? rowDate.getTime() <= today.getTime()
          : rowDate.getTime() === today.getTime())
      );
    }

    const rowValue = this.normalizeComparableValue(row[field]);
    const matches = Array.isArray(rule.equals)
      ? rule.equals
      : Array.isArray(rule.values)
        ? rule.values
        : rule.value !== undefined
          ? [rule.value]
          : [];

    return matches
      .map((value: any) => this.normalizeComparableValue(value))
      .includes(rowValue);
  }

  shouldShowHeaderInfo(column: any): boolean {
    return (
      !!column?.info ||
      !!column?.tooltipText ||
      !!column?.tooltip ||
      !!column?.tooltipUrl
    );
  }

  shouldShowHeaderImageTooltip(column: any): boolean {
    return !!column?.tooltipUrl || column?.tooltip === 'imageTooltip';
  }

  getHeaderTooltipText(column: any): string {
    return String(column?.tooltipText || column?.name || '').trim();
  }

  // Toggle one row
  toggleRow(row: any) {
    const idx = this.selectedRows.indexOf(row);
    if (idx >= 0) {
      this.selectedRows.splice(idx, 1); // unselect
    } else {
      this.selectedRows.push(row); // select
    }
    // keep header checkbox in sync
    this.selectAllChecked =
      this.rows?.length > 0 && this.selectedRows.length === this.rows.length;
    // recompute total
    this.updateTotalBudget();
    // emit
    this.rowsSelected.emit(this.selectedRows);
  }

  // Check if a row is selected
  isSelected(row: any): boolean {
    return this.selectedRows.indexOf(row) >= 0;
  }

  // Select/unselect all rows
  toggleSelectAll(checked: boolean) {
    this.selectAllChecked = checked;
    if (checked) {
      this.selectedRows = [...this.rows];
    } else {
      this.selectedRows = [];
    }
    this.updateTotalBudget();
    this.rowsSelected.emit(this.selectedRows);
  }

  // For indeterminate UI if you later switch header binding to [indeterminate]
  isAllSelected(): boolean {
    return (
      this.rows?.length > 0 && this.selectedRows.length === this.rows.length
    );
  }

  isIndeterminate(): boolean {
    return this.selectedRows.length > 0 && !this.isAllSelected();
  }

  NotInM: boolean = false;

  private loadAndRenderChart(filters?: any): void {
    this.isLoading = true;
    if (this.data.multiHeader) {
      this.configService.getConfigData(this.data.headerUrl).subscribe({
        next: (res: any) => {
          const filterKey = filters.source[0].toLowerCase();
          if (res[filterKey]) {
            this.data.attributeNames = res[filterKey].attributes || [];
            this.data.headers = res[filterKey].headers || [];
            this.resetPinnedColumnState();

            // Set `NotInM` to true if any column name starts with "Investment Estimate" or "Investment Split"
            this.NotInM = this.data.headers.some(
              (col: any) =>
                col.name?.startsWith('Investment Estimate') ||
                col.name?.startsWith('Investment Split')
            );

            // Merge additional filters and load rows
            this.filters = {
              ...this.filters,
              ...this.data.additionalFilters.reduce(
                (acc: any, obj: any) => ({ ...acc, ...obj }),
                {}
              ),
            };
            this.getRows();
          } else {
            this.data.attributeNames = [];
            this.data.headers = [];
            this.resetPinnedColumnState();
            this.NotInM = false;
          }
        },
        error: () => {
          this.NotInM = false;
          this.isLoading = false;
        },
      });
    } else {
      this.getRows();
      this.NotInM = false;
    }
  }

  /** Recalculate total budget from selected rows' total_cost (robust to strings with commas) */
  private updateTotalBudget() {
    const configuredBudgetProp = String(this.data?.totalBudgetProp ?? '').trim();
    if (configuredBudgetProp) {
      const divisor = Number(this.data?.totalBudgetDivisor);
      const safeDivisor = Number.isFinite(divisor) && divisor > 0 ? divisor : 1;
      const total = this.selectedRows.reduce(
        (sum, row) => sum + this.parseNumeric(row?.[configuredBudgetProp]),
        0
      );

      // Dollar-based totals can require up to six decimal places when shown in millions.
      this.totalBudget = Number((total / safeDivisor).toFixed(6));
      return;
    }

    this.totalBudget = this.selectedRows.reduce((sum, row) => {
      const estimateValue = this.isSimulationPlanningPage()
        ? row?.simulation_investment_estimate
        : this.isAopPlanningPage()
          ? row?.aop_investment_estimate
          : this.isIdeasHopperPage()
            ? row?.ideation_investment_estimate
            : row?.investment_estimate;
      const estimateCost = this.parseNumeric(estimateValue);

      // Prefer page-specific estimate values when available.
      if (estimateCost !== 0) {
        return sum + estimateCost;
      }

      if (this.NotInM) {
        const cost1 = this.parseNumeric(row?.investment_testing);
        return sum + cost1;
      } else {
        const cost = this.parseNumeric(row?.total_cost);
        // Legacy tables expose total_cost in dollars; convert to million dollars.
        const finalCost = cost / 1_000_000;
        return sum + finalCost;
      }
    }, 0);

    // Format the total budget with 1 decimal place
    this.totalBudget = parseFloat(this.totalBudget.toFixed(1));
  }

  availableBudget: any;

  @ViewChild('table') table: any;

  onBudgetInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    this.availableBudget = value;
    // Force table refresh
    if (this.table) {
      this.table.recalculate();
      this.table.bodyComponent.updateRows();
    }
    this.computeHighlightIndex();
    this.cdr.detectChanges();
  }

  highlightIndex = -1;
  indexMap = new Map<string, number>();

  private hasRowField(field: string): boolean {
    return this.rows?.some((row: any) => row && row[field] !== undefined);
  }

  private resolveScoreKey(): string {
    const headerProps = new Set(
      (this.data?.headers || []).map((column: any) => String(column?.prop || ''))
    );

    const scoreCandidates = [
      'aop_final_score',
      'ideation_final_score',
      'simulation_final_score',
      'final_score',
    ];

    for (const key of scoreCandidates) {
      if (headerProps.has(key) || this.hasRowField(key)) {
        return key;
      }
    }

    return 'final_score';
  }

  private resolveHighlightBudgetKey(): string {
    const configured = String(this.data?.highlightKey || '').trim();
    if (configured) {
      return configured;
    }

    const budgetCandidates = [
      'aop_investment_estimate',
      'ideation_investment_estimate',
      'simulation_investment_estimate',
      'investment_estimate',
      'total_cost',
    ];

    for (const key of budgetCandidates) {
      if (this.hasRowField(key)) {
        return key;
      }
    }

    return '';
  }

  computeHighlightIndex() {
    const budgetKey = this.resolveHighlightBudgetKey();
    const scoreKey = this.resolveScoreKey();

    if (
      this.availableBudget == null ||
      !budgetKey ||
      !this.rows?.length
    ) {
      this.highlightIndex = -1;
      this.indexMap.clear();
      return;
    }

    // Sort rows by the best available score field for this table/page.
    this.rows.sort((a, b) => {
      const scoreA = parseFloat(a?.[scoreKey]);
      const scoreB = parseFloat(b?.[scoreKey]);
      if (isNaN(scoreA)) return 1;
      if (isNaN(scoreB)) return -1;
      return scoreB - scoreA;
    });

    let total = 0;
    let lastIndex = -1;

    for (let i = 0; i < this.rows.length; i++) {
      const rawValue = this.rows[i][budgetKey];
      if (rawValue == null || isNaN(rawValue)) continue;

      const value = parseFloat(Number(rawValue).toFixed(4));
      if (isNaN(value)) continue;
      const newTotal = total + value;
      if (newTotal > this.availableBudget) {
        break;
      }

      total = newTotal;
      lastIndex = i;
    }

    this.highlightIndex = lastIndex;

    // Build fast lookup map: uuid -> rowIndex
    this.indexMap.clear();
    this.rows.forEach((row, i) => {
      this.indexMap.set(row.uuid, i);
    });

    // Force table refresh if needed
    this.rows = [...this.rows];
  }

  // Called by ngx-datatable for each row
  getRowClass = (row: any) => {
    if (this.highlightIndex === -1 || !row?.uuid) return '';

    const index = this.indexMap.get(row.uuid);
    if (index === undefined) return '';

    return index <= this.highlightIndex ? 'highlight-row' : '';
  };

  getRows() {
    if (this.shouldUseLocalRows()) {
      this.rows = this.normalizeLoadedRows(this.data?.staticRows || []);
      this.originalRows = this.cloneRows(this.rows);
      this.fullData = this.rows;
      this.selectedRows = [];
      this.selectAllChecked = false;
      this.totalBudget = 0;
      this.hasError = this.rows.length === 0;
      this.errorMessage = this.hasError ? 'No data found for the selected filters.' : '';
      this.isLoading = false;
      this.clearUiCaches();
      this.applyFilters();
      this.scheduleTableRecalculate();
      return;
    }

    this.data.costFilter = this.data.singleCostFilter
      ? this.selectedFilters[0] ?? 'ALL'
      : this.data.costFilter
      ? this.selectedFilters
      : null;
    const initialLoadAction = this.getInitialLoadAction();
    if (initialLoadAction?.payload) {
      const payload = this.replacePayloadTokens(
        initialLoadAction.payload,
        this.buildPayloadTokenReplacements({})
      );

      this.configService
        .customApi(
          initialLoadAction.url || this.data?.apiUrl,
          payload,
          this.data,
          initialLoadAction.method || 'post'
        )
        .subscribe({
          next: (res) => this.applyRowsResponse(res),
          error: () => {
            this.hasError = true;
            this.isLoading = false;
            this.errorMessage = initialLoadAction.errorMessage || 'No Data available for the table.';
          },
        });
      return;
    }

    this.configService
      .fetchData(
        this.data.apiUrl,
        this.data.functionName,
        this.filters,
        this.data,
        'post',
        undefined,
        this.email
      )
      .subscribe({
        next: (res) => {
          this.applyRowsResponse(res);
        },
        error: () => {
          this.hasError = true;
          this.isLoading = false;
          this.errorMessage = 'No Data available for the table.';
        },
      });
  }

  private getInitialLoadAction(): any {
    if (this.data?.initialLoadAction?.payload) {
      return this.data.initialLoadAction;
    }

    const actionIndex = Number(this.data?.initialLoadPostSaveActionIndex);
    if (
      Number.isInteger(actionIndex) &&
      Array.isArray(this.data?.postSaveActions) &&
      this.data.postSaveActions[actionIndex]?.payload
    ) {
      return this.data.postSaveActions[actionIndex];
    }

    return null;
  }

  private applyRowsResponse(res: any): void {
    const primaryPayload = res?.responseList?.[0]?.responseData?.[0];
    this.applyResponseEditAccess(primaryPayload ?? res);
    const responseErrorMessage = this.getResponseErrorMessage(
      primaryPayload,
      res
    );
    const extractedRows = this.extractRowsFromResponse(res, primaryPayload);

    this.rows = this.normalizeLoadedRows(extractedRows);

    this.originalRows = this.cloneRows(this.rows);
    this.clearUiCaches();
    // reset selection + totals whenever data reloads
    this.selectedRows = [];
    this.selectAllChecked = false;
    this.totalBudget = 0;

    this.fullData = this.isErrorPayload(primaryPayload)
      ? []
      : primaryPayload ?? [];
    const isEmpty = !this.rows || !this.rows?.length;
    this.isLoading = false;
    if (isEmpty) {
      this.hasError = true;
      this.errorMessage =
        responseErrorMessage || 'No data found for the selected filters.';
    } else {
      this.hasError = false;
    }
    this.computeHighlightIndex();
    this.applyFilters();
    this.scheduleTableRecalculate();
  }

  enterEdit() {
    if (this.isEditMode) return;
    this.isEditMode = true;
    this.rowHeightCache = new WeakMap<object, number>();
    this.displayedColumnsCache = null;
    this.renderedColumnsCache = null;
    this.resetSelectionState();
    this.scheduleTableRecalculate();
  }

  exitEdit() {
    if (!this.isEditMode) return;
    this.isEditMode = false;
    this.rowHeightCache = new WeakMap<object, number>();
    this.displayedColumnsCache = null;
    this.renderedColumnsCache = null;
    this.resetSelectionState();

    // Fast path: switching tabs without edits should not trigger a network reload.
    if (!this.changed) {
      return;
    }

    // Discard unsaved edits locally by restoring the last loaded snapshot.
    this.rows = this.cloneRows(this.originalRows).map((row: any) => ({
      ...row,
      row_status: 'unchanged',
    }));
    this.changed = false;
    this.note = '';
    this.computeHighlightIndex();
    this.applyFilters();
    this.scheduleTableRecalculate();
  }

  private resetSelectionState(): void {
    this.selection.clear();
    this.selectedRows = [];
    this.selectAllChecked = false;
    this.totalBudget = 0;
  }

  private cloneRows(rows: any[]): any[] {
    if (!Array.isArray(rows)) return [];

    return rows.map((row: any) => {
      const clonedRow: any = {};

      Object.keys(row || {}).forEach((key) => {
        const value = row[key];
        if (Array.isArray(value)) {
          clonedRow[key] = [...value];
        } else if (value && typeof value === 'object') {
          clonedRow[key] = { ...value };
        } else {
          clonedRow[key] = value;
        }
      });

      return clonedRow;
    });
  }

  private sortRows(rows: any[], prop: string, dir: 'asc' | 'desc'): any[] {
    const sortedRows = [...rows];
    sortedRows.sort((a, b) => {
      let valA = Array.isArray(a?.[prop]) ? a?.[prop].join(', ') : a?.[prop];
      let valB = Array.isArray(b?.[prop]) ? b?.[prop].join(', ') : b?.[prop];

      if (valA === null || valA === undefined || valA === '') return 1;
      if (valB === null || valB === undefined || valB === '') return -1;

      const numA = Number(valA);
      const numB = Number(valB);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        valA = numA;
        valB = numB;
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }

      if (valA < valB) return dir === 'asc' ? -1 : 1;
      if (valA > valB) return dir === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedRows;
  }

  private applyCurrentSort(): void {
    if (!this.sortState.prop || !this.sortState.dir) {
      return;
    }

    const hasSortableColumn = (this.renderedColumns || []).some(
      (column: any) => String(column?.prop || '') === this.sortState.prop
    );

    if (!hasSortableColumn) {
      return;
    }

    this.rows = this.sortRows(this.rows, this.sortState.prop, this.sortState.dir);
  }

  sortColumn(column: any, event?: MouseEvent): void {
    event?.stopPropagation();

    if (!column?.prop || column.sortable === false) {
      return;
    }

    const nextDir: 'asc' | 'desc' =
      this.sortState.prop === column.prop && this.sortState.dir === 'asc'
        ? 'desc'
        : 'asc';

    this.onSort({ sorts: [{ prop: column.prop, dir: nextDir }] });
  }

  isColumnSorted(column: any): boolean {
    return !!column?.prop && this.sortState.prop === column.prop && !!this.sortState.dir;
  }

  getSortIcon(column: any): string {
    if (!this.isColumnSorted(column) || !this.sortState.dir) {
      return 'swap_vert';
    }

    return this.sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getSortTooltipText(column: any): string {
    if (!this.isColumnSorted(column)) {
      return 'Sort column';
    }

    return this.sortState.dir === 'asc' ? 'Sort descending' : 'Sort ascending';
  }

  private getTableRowHeight(row?: any): number {
    if (this.isEditMode && row && this.activeCommentEditorRow === row) {
      return 160;
    }
    return this.isEditMode ? 56 : 44;
  }

  isCommentEditorActive(row: any): boolean {
    return this.activeCommentEditorRow === row;
  }

  onCommentEditorMouseEnter(row: any): void {
    if (this.activeCommentEditorFocusLocked && this.activeCommentEditorRow !== row) return;
    this.setActiveCommentEditorRow(row);
  }

  onCommentEditorMouseLeave(row: any): void {
    if (this.activeCommentEditorFocusLocked) return;
    this.clearActiveCommentEditorRow(row);
  }

  onCommentEditorFocusIn(row: any): void {
    this.activeCommentEditorFocusLocked = true;
    this.setActiveCommentEditorRow(row);
  }

  onCommentEditorFocusOut(event: FocusEvent, row: any): void {
    const host = event.currentTarget as HTMLElement | null;
    const next = event.relatedTarget as Node | null;
    if (host && next && host.contains(next)) return;
    this.activeCommentEditorFocusLocked = false;
    this.clearActiveCommentEditorRow(row);
  }

  private setActiveCommentEditorRow(row: any): void {
    if (this.activeCommentEditorRow === row) return;
    this.activeCommentEditorRow = row;
    this.refreshTableRows();
  }

  private clearActiveCommentEditorRow(row: any): void {
    if (this.activeCommentEditorRow !== row) return;
    this.activeCommentEditorRow = null;
    this.refreshTableRows();
  }

  private refreshTableRows(): void {
    this.rowHeightCache = new WeakMap<object, number>();
    this.cdr.detectChanges();
    setTimeout(() => {
      this.table?.recalculate?.();
      this.table?.bodyComponent?.updateRows?.();
    });
  }

  private estimateReadOnlyRowHeight(row: any): number {
    const minHeight = 44;
    const lineHeight = 18;
    const verticalPadding = 18;
    const maxLinesCap = 6;
    let maxLines = 1;

    for (const column of this.renderedColumns) {
      if (!column?.prop || this.isDeleteColumn(column)) {
        continue;
      }

      const cellText = this.getRowHeightCellText(row, column);
      if (!cellText) {
        continue;
      }

      const columnWidth = Number(column.width || this.getDefaultColumnWidth());
      const usableWidth = Math.max(72, columnWidth - 24);
      const charsPerLine = Math.max(10, Math.floor(usableWidth / 7));
      const lineCount = cellText
        .split(/\r?\n/)
        .reduce((total: number, line: string) => {
          const normalizedLine = line.trim();
          if (!normalizedLine) {
            return total + 1;
          }

          return total + Math.max(1, Math.ceil(normalizedLine.length / charsPerLine));
        }, 0);

      maxLines = Math.max(maxLines, Math.min(maxLinesCap, lineCount));
    }

    return Math.max(minHeight, verticalPadding + maxLines * lineHeight);
  }

  private getRowHeightCellText(row: any, column: any): string {
    const value = row?.[column?.prop];

    if (value === null || value === undefined) {
      return '';
    }

    if (column?.type === 'date') {
      return value ? String(value) : '';
    }

    if (column?.type === 'number') {
      return Number.isFinite(value) ? String(value) : String(value ?? '');
    }

    return this.formatCellValue(value);
  }

  get displayedColumns(): string[] {
    const visibleHeaders = this.getVisibleHeaders();
    const headerSignature = visibleHeaders
      .map((col: any) => `${col?.prop}:${col?.showOnEdit ? 1 : 0}:${(col?.visibleRoles || []).join(',')}`)
      .join('|');
    const cacheKey = `${this.isEditMode ? 'edit' : 'view'}::role:${this.currentUserRole}::${headerSignature}`;

    if (this.displayedColumnsCache?.key === cacheKey) {
      return this.displayedColumnsCache.value;
    }

    const cols = [
      'sno',
      ...visibleHeaders
        .filter((col: any) => !(col.showOnEdit && !this.isEditMode))
        .map((col: any) => col.prop),
    ];

    if (this.isEditMode) {
      cols.push('delete'); // only in edit mode
    }
    this.displayedColumnsCache = { key: cacheKey, value: cols };
    return cols;
  }

  get renderedColumns(): any[] {
    const visibleHeaders = this.getVisibleHeaders();
    const headerSignature = visibleHeaders
      .map((col: any) => `${col?.prop}:${col?.showOnEdit ? 1 : 0}:${(col?.visibleRoles || []).join(',')}`)
      .join('|');
    const hasDeleteColumn =
      this.isEditMode &&
      this.data?.buttons?.includes('delete') &&
      this.data?.deleteColumnAtEnd !== true;
    const cacheKey = `${this.isEditMode ? 'edit' : 'view'}::role:${this.currentUserRole}::${headerSignature}::delete:${hasDeleteColumn ? 1 : 0}`;

    if (this.renderedColumnsCache?.key === cacheKey) {
      return this.renderedColumnsCache.value;
    }

    const columns = [
      ...visibleHeaders.filter(
        (col: any) => !(col.showOnEdit && !this.isEditMode)
      ),
    ];

    if (hasDeleteColumn) {
      columns.push({
        name: 'Delete',
        prop: 'delete',
        width: 80,
        sortable: false,
      });
    }

    this.renderedColumnsCache = { key: cacheKey, value: columns };
    return columns;
  }

  onCellValueChange(rowIndex: number, prop: string, type: string, value: any) {
    let parsedValue: any = value;
    if (type === 'number') {
      // Keep raw value while typing to preserve caret position, but cap to 4 decimals.
      parsedValue = this.isIntegerColumn(prop)
        ? this.limitToIntegerValue(value)
        : this.limitToMaxDecimals(value, 4);
    }
    this.updateCell(rowIndex, prop, parsedValue);
  }

  onInputValueChange(
    event: Event,
    rowIndex: number,
    prop: string,
    type: string
  ) {
    const input = event.target as HTMLInputElement;
    this.onCellValueChange(rowIndex, prop, type, input.value);

    if (type === 'number') {
      const normalized = this.rows[rowIndex]?.[prop] ?? '';
      if (input.value !== String(normalized)) {
        input.value = String(normalized);
      }
    }
  }

  private limitToMaxDecimals(value: any, maxDecimals: number): any {
    if (value === '' || value === null || value === undefined) return value;

    const raw = String(value).trim();
    if (raw === '-' || raw === '.' || raw === '-.') return raw;

    // Handle scientific notation safely for number inputs (e.g. 1e-7).
    if (/e/i.test(raw)) {
      const expValue = Number(raw);
      if (Number.isNaN(expValue)) return '';
      return Number(expValue.toFixed(maxDecimals));
    }

    const isNegative = raw.startsWith('-');
    const numericBody = raw.replace(/[^0-9.]/g, '');
    const firstDotIndex = numericBody.indexOf('.');

    const normalizedBody =
      firstDotIndex === -1
        ? numericBody
        : `${numericBody.slice(0, firstDotIndex)}.${numericBody
          .slice(firstDotIndex + 1)
          .replace(/\./g, '')}`;

    let normalized = isNegative ? `-${normalizedBody}` : normalizedBody;
    if (normalized === '' || normalized === '-') return normalized;

    const decimalIndex = normalized.indexOf('.');
    if (decimalIndex === -1) return normalized;

    const integerPart = normalized.slice(0, decimalIndex);
    const decimalPart = normalized.slice(decimalIndex + 1);
    return `${integerPart}.${decimalPart.slice(0, maxDecimals)}`;
  }

  private limitToIntegerValue(value: any): any {
    if (value === '' || value === null || value === undefined) return value;

    const raw = String(value).trim();
    if (raw === '-') return raw;

    if (/e/i.test(raw)) {
      const expValue = Number(raw);
      if (Number.isNaN(expValue)) return '';
      return String(Math.trunc(expValue));
    }

    const isNegative = raw.startsWith('-');
    const integerPart = raw.replace(/^-/, '').split('.')[0];
    const digits = integerPart.replace(/[^0-9]/g, '');
    if (!digits) return isNegative ? '-' : '';

    return `${isNegative ? '-' : ''}${digits}`;
  }

  private isIntegerColumn(prop: string): boolean {
    const column = this.getColumnByProp(prop);
    return column?.integer === true || column?.dataType === 'integer';
  }

  private getColumnByProp(prop: string): any {
    return (this.data?.headers || []).find(
      (header: any) => header?.prop === prop
    );
  }

  getNumberInputStep(column: any): string | null {
    if (column?.type !== 'number') return null;
    return column?.integer === true || column?.dataType === 'integer'
      ? '1'
      : '0.0001';
  }

  private roundToMaxDecimals(value: number, maxDecimals: number): number {
    if (!Number.isFinite(value)) return 0;
    return Number(value.toFixed(maxDecimals));
  }

  private resolveInvestmentEstimateProp(row: any): string {
    const headerProps: string[] = (this.data?.headers || [])
      .map((col: any) => col?.prop)
      .filter((prop: any) => typeof prop === 'string' && prop.length > 0);

    const knownKeys = [
      'investment_estimate',
      'ideation_investment_estimate',
      'aop_investment_estimate',
      'simulation_investment_estimate',
    ];

    const dynamicKeys = headerProps.filter((prop) =>
      /investment_estimate$/i.test(prop)
    );

    const candidates = [...new Set([...knownKeys, ...dynamicKeys])];
    const rowKeys = Object.keys(row || {});

    const fromRow = candidates.find((key) => rowKeys.includes(key));
    if (fromRow) return fromRow;

    const fromHeader = candidates.find((key) => headerProps.includes(key));
    if (fromHeader) return fromHeader;

    return 'investment_estimate';
  }

  private setInvestmentEstimate(row: any, value: number): void {
    const estimateProp = this.resolveInvestmentEstimateProp(row);
    row[estimateProp] = this.roundToMaxDecimals(value, 4);
  }
  private roundToDecimals(value: number, decimals: number = 4): number {
    if (!Number.isFinite(value)) return 0;
    return Number(value.toFixed(decimals));
  }

  onSelectChange(
    event: any,
    rowIndex: number,
    prop: string,
    column: any
  ): void {
    if (column.isMultiSelect) {
      this.rows[rowIndex][prop] = Array.isArray(event) ? event : [event];
    } else {
      this.rows[rowIndex][prop] = event;
    }

    this.updateCell(rowIndex, prop, this.rows[rowIndex][prop]);
  }

  private getProjectCategory(): string {
    const additionalFilters = this.data?.additionalFilters;
    if (!Array.isArray(additionalFilters)) return '';

    const projectCategoryFilter = additionalFilters.find(
      (filter: any) => filter?.project_category
    );

    const categoryValue = projectCategoryFilter?.project_category;
    if (Array.isArray(categoryValue)) {
      return String(categoryValue[0] ?? '').toLowerCase();
    }

    return String(categoryValue ?? '').toLowerCase();
  }

  private isAopPlanningPage(): boolean {
    if (this.getProjectCategory() === 'aop') return true;

    const headerProps = new Set(
      (this.data?.headers || []).map((column: any) => String(column?.prop || ''))
    );
    const attributeNames = Array.isArray(this.data?.attributeNames)
      ? this.data.attributeNames.map((name: any) => String(name || ''))
      : [];

    return (
      headerProps.has('aop_final_score') ||
      headerProps.has('aop_investment_estimate') ||
      attributeNames.includes('aop_final_score') ||
      attributeNames.includes('aop_investment_estimate')
    );
  }

  private isIdeasHopperPage(): boolean {
    return this.getProjectCategory() === 'ideation';
  }

  private isSimulationPlanningPage(): boolean {
    return this.getProjectCategory() === 'simulation';
  }

  updateCell(rowIndex: number, prop: string, value: any) {
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    const row = this.rows[rowIndex];
    const column = this.getColumnByProp(prop);
    if (this.isCellEditFrozen(column, row, rowIndex)) return;
    if (row && typeof row === 'object') {
      this.rowHeightCache.delete(row);
    }
    this.rows[rowIndex][prop] = value;
    this.applyYearDateConstraints(rowIndex, prop);
    this.applyDateRangeConstraints(rowIndex, prop);
    this.applyDateInputConstraints(rowIndex, prop);
    if (
      prop === 'is_project_in_wave' &&
      this.normalizeComparableValue(value) !== 'yes' &&
      this.rows[rowIndex]?.wave_number
    ) {
      this.rows[rowIndex].wave_number = '';
    }
    if (this.isPlanningYearTarget({ prop })) {
      if (String(value).toUpperCase() === 'AOP') {
        this.loadPlanningYearOptions();
      } else if (!this.rowTargetsAop(this.rows[rowIndex])) {
        delete this.rows[rowIndex][this.getPlanningYearKey()];
      }
    }
    if (this.rows[rowIndex].row_status !== 'new') {
      this.rows[rowIndex].row_status = 'edited';
    }
    // Auto-update total investment if relevant fields are updated
    if (
      ['investment_parts', 'investment_people', 'investment_testing'].includes(
        prop
      )
    ) {
      const row = this.rows[rowIndex];
      const parts = Number(row['investment_parts']) || 0;
      const people = Number(row['investment_people']) || 0;
      const testing = Number(row['investment_testing']) || 0;
      this.setInvestmentEstimate(row, parts + people + testing);
      const total = this.roundToDecimals(parts + people + testing, 4);

      if (this.isAopPlanningPage()) {
        row['aop_investment_estimate'] = total;
      }

      if (this.isIdeasHopperPage()) {
        row['ideation_investment_estimate'] = total;
      }
    }
    if (
      [
        'investment_year_26',
        'investment_year_27',
        'investment_year_28',
        'investment_year_29',
        'investment_year_30',
        'investment_year_31',
      ].includes(prop)
    ) {
      const row = this.rows[rowIndex];
      const x2 = Number(row['investment_year_27']) || 0;
      const x3 = Number(row['investment_year_28']) || 0;
      const x4 = Number(row['investment_year_29']) || 0;
      const x5 = Number(row['investment_year_30']) || 0;
      const x6 = Number(row['investment_year_31']) || 0;
      this.setInvestmentEstimate(row, x2 + x3 + x4 + x5 + x6);
      const total = this.roundToDecimals(x2 + x3 + x4 + x5 + x6, 4);

      if (this.isSimulationPlanningPage()) {
        row['simulation_investment_estimate'] = total;
      }

    }
    this.applyDerivedRowValues(this.rows[rowIndex], prop);
    this.note = this.getUnsavedNote();
    this.changed = true;
  }

  isPlanningYearTarget(column: any): boolean {
    const targetProps = this.data?.planningYearSelector?.targetProps || [];
    return targetProps.includes(column?.prop);
  }

  onAopOptionHover(rowIndex: number, column: any, option: any): void {
    const value = option?.value ?? option;
    if (!this.isPlanningYearTarget(column) || String(value).toUpperCase() !== 'AOP') return;
    this.hoveredAopCell = `${rowIndex}:${column.prop}`;
    this.loadPlanningYearOptions();
  }

  isPlanningYearOption(column: any, option: any): boolean {
    const value = option?.value ?? option;
    return this.isPlanningYearTarget(column) && String(value).toUpperCase() === 'AOP';
  }

  keepPlanningYearFlyoutOpen(rowIndex: number, column: any): void {
    this.hoveredAopCell = `${rowIndex}:${column.prop}`;
  }

  isPlanningYearSelected(rowIndex: number, year: number): boolean {
    return Number(this.rows[rowIndex]?.[this.getPlanningYearKey()]) === Number(year);
  }

  getPlanningYearTooltip(rowIndex: number, column: any): string {
    if (!this.isPlanningYearTarget(column)) return '';
    const displayValue = this.getPlanningDropdownDisplay(rowIndex, column);
    return displayValue ? ` ${displayValue}` : '';
  }

  getPlanningDropdownDisplay(rowIndex: number, column: any): string {
    const row = this.rows[rowIndex];
    const selectedValue = row?.[column?.prop];
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return '';
    }

    const selectedOption = (column?.list || []).find(
      (option: any) => (option?.value ?? option) === selectedValue
    );
    const label =
      selectedOption?.viewValue ??
      selectedOption?.value ??
      selectedOption ??
      selectedValue;
    const year = row?.[this.getPlanningYearKey()];

    return String(selectedValue).toUpperCase() === 'AOP' &&
      year !== null && year !== undefined && year !== ''
      ? `${label} - ${year}`
      : String(label);
  }

  shouldShowPlanningYears(rowIndex: number, column: any, option: any): boolean {
    const value = option?.value ?? option;
    return this.isPlanningYearTarget(column) &&
      String(value).toUpperCase() === 'AOP' &&
      this.hoveredAopCell === `${rowIndex}:${column.prop}`;
  }

  selectPlanningYear(
    event: MouseEvent,
    rowIndex: number,
    column: any,
    year: number,
    select: MatSelect
  ): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isRowEditFrozen(this.rows[rowIndex], rowIndex)) return;
    this.rows[rowIndex][column.prop] = 'AOP';
    this.updateCell(rowIndex, this.getPlanningYearKey(), year);
    this.hoveredAopCell = '';
    select.close();
  }

  private getPlanningYearKey(): string {
    return this.data?.planningYearSelector?.requestKey || 'ecosystem_active_year';
  }

  private rowTargetsAop(row: any): boolean {
    const targetProps = this.data?.planningYearSelector?.targetProps || [];
    return targetProps.some((prop: string) => String(row?.[prop]).toUpperCase() === 'AOP');
  }

  private loadPlanningYearOptions(): void {
    if (this.planningYearRequestStarted) return;
    this.planningYearRequestStarted = true;
    this.planningYearLoading = true;
    this.planningYearError = '';
    const selector = this.data?.planningYearSelector;
    const payload = {
      entityName: 'entity_store',
      attributesList: ['payload'],
      filterList: [{
        operator: 'IS NOT',
        attributeName: 'uuid',
        attributeValue: 'null',
        logicalOperator: '',
      }],
    };

    this.configService
      .getEntityStoreMetadata(selector?.apiUrl || '/spriced/platform/getData', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const attribute = this.findAttributeMetadata(response, this.getPlanningYearKey());
          const visibleYears = this.getConfiguredVisiblePlanningYears(selector);
          this.planningYearOptions = (attribute?.validValues || [])
            .map((value: any) => Number(value))
            .filter((value: number) =>
              Number.isFinite(value) &&
              (!visibleYears.length || visibleYears.includes(value))
            );
          this.planningYearLoading = false;
          if (!this.planningYearOptions.length) this.planningYearError = 'No active years available';
        },
        error: () => {
          this.planningYearLoading = false;
          this.planningYearError = 'Unable to load active years';
          this.planningYearRequestStarted = false;
        },
      });
  }

  private getConfiguredVisiblePlanningYears(selector: any): number[] {
    return (Array.isArray(selector?.visibleYears) ? selector.visibleYears : [])
      .map((value: any) => Number(value))
      .filter((value: number) => Number.isFinite(value));
  }

  private findAttributeMetadata(value: any, name: string): any | null {
    if (value == null) return null;
    if (typeof value === 'string') {
      try { return this.findAttributeMetadata(JSON.parse(value), name); } catch { return null; }
    }
    if (typeof value !== 'object') return null;
    if (value.name === name && Array.isArray(value.validValues)) return value;
    for (const child of Object.values(value)) {
      const match = this.findAttributeMetadata(child, name);
      if (match) return match;
    }
    return null;
  }

  private findEntityMetadata(value: any, entityName: string): any | null {
    if (value == null) return null;
    if (typeof value === 'string') {
      try { return this.findEntityMetadata(JSON.parse(value), entityName); } catch { return null; }
    }
    if (typeof value !== 'object') return null;
    if (value.entity === entityName && Array.isArray(value.attributesList)) return value;
    for (const child of Object.values(value)) {
      const match = this.findEntityMetadata(child, entityName);
      if (match) return match;
    }
    return null;
  }

  deleteRow(index: number) {
    if (this.isDeleteDisabled(this.rows[index], index)) return;
    if (this.data?.deleteAction?.confirmBeforeApi === true) {
      this.confirmAndDeleteRow(index);
      return;
    }
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    this.rows[index].is_deleted = true;
    this.rows = [...this.rows];
    this.rows[index].row_status = 'deleted';
    this.note = this.getUnsavedNote();
    this.changed = true;
  }

  private confirmAndDeleteRow(index: number): void {
    const row = this.rows[index];
    const action = this.data?.deleteAction || {};
    const surveyLabel = row?.[action.labelProp || 'survey_number'] || 'this survey';
    const dialogRef = this.dialog.open(ConfirmDialogueComponent, {
      width: action.confirmWidth || '420px',
      disableClose: true,
      data: {
        title: action.confirmTitle || 'Delete Survey',
        desc: (action.confirmMessage || 'Do you want to delete {value}?').replace(
          '{value}',
          surveyLabel
        ),
        productIds: [],
        noLabel: action.cancelLabel || 'Cancel',
        yesLabel: action.okLabel || 'OK',
        showCloseButton: true,
        showContinueText: false,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.callDeleteApi(index);
    });
  }

  private callDeleteApi(index: number): void {
    if (!this.data?.saveUrl || !this.data?.saveFunction || !this.data?.savePayloadConfig) {
      this.snackBar.open('Delete API is not configured yet.', 'Close', {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error'],
      });
      return;
    }

    const row = this.rows[index];
    this.isLoading = true;
    const rowPayload = { ...row, __row_status: 'deleted', is_deleted: true };
    this.configService
      .saveData([rowPayload], this.filters?.source?.[0], this.data, this.email)
      .subscribe({
        next: (res: any) => {
          const commitStatus = this.getCommitStatus(res);
          if (commitStatus && commitStatus !== 'COMMIT_SUCCESS') {
            const errorStack = this.getErrorStackMessage(res);
            this.isLoading = false;
            this.snackBar.open(errorStack || `Failed to Delete: Commit Status ${commitStatus}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
            return;
          }

          this.rows = this.rows.filter((_row, rowIndex) => rowIndex !== index);
          this.originalRows = this.cloneRows(this.rows);
          this.clearUiCaches();
          this.changed = this.rows.some((item: any) =>
            ['edited', 'new', 'deleted'].includes(String(item?.row_status || ''))
          );
          this.note = this.changed ? this.note : '';
          this.isLoading = false;
          this.snackBar.open('Survey deleted successfully!', 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-success'],
          });
        },
        error: (err) => {
          console.error('Delete failed:', err);
          this.isLoading = false;
          const errorStack = this.getErrorStackMessage(err?.error || err);
          this.snackBar.open(errorStack || 'Failed to delete.', 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  addRow() {
    const newRow: any = {};
    this.data.headers.forEach((col: any) => {
      if (col.prop !== 'delete') {
        if (col.defaultValue !== undefined) {
          newRow[col.prop] = col.defaultValue;
        } else if (col.type === 'number') {
          newRow[col.prop] = 0;
        } else if (col.type === 'dropdown') {
          //  set default selected value
          newRow[col.prop] = col.selected ?? '';
        } else if (col.type === 'date') {
          newRow[col.prop] = null;
        } else {
          newRow[col.prop] = '';
        }
      }
    });
    newRow['row_status'] = 'new';
    this.rows = [newRow, ...this.rows];
    this.resetToFirstPage();
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    this.note = this.getUnsavedNote();
    this.changed = true;
  }

  private resetToFirstPage(): void {
    if (!this.table) return;
    this.table.offset = 0;
    this.table.bodyComponent?.updateOffsetY(0);
    this.table.bodyComponent?.updateRows();
  }

  onDateChange(rowIndex: number, prop: string, date: Date | null) {
    this.updateCell(rowIndex, prop, date);
  }

  selectFilter(filter: string, checked: boolean) {
    if (checked) {
      if (this.data.singleCostFilter) {
        this.selectedFilters = [filter];
      } else if (!this.selectedFilters.includes(filter)) {
        this.selectedFilters.push(filter);
      }
    } else {
      // Remove unchecked filter
      this.selectedFilters = this.selectedFilters.filter(
        (f: any) => f !== filter
      );
    }

    // Reload your chart with the updated active filters
    this.loadAndRenderChart({
      ...this.filters,
      activeFilters: [...this.selectedFilters], // spread to avoid mutation
    });
  }

  save() {
    if (this.hasResponseControlledEditAccess() && !this.isAlwaysEditRole()) {
      this.refreshEditAccessOnly((canEdit) => {
        if (!canEdit) {
          this.showEditLockedMessage();
          return;
        }

        this.saveAfterEditAccessCheck();
      }, true);
      return;
    }

    this.saveAfterEditAccessCheck();
  }

  saveRow(row: any): void {
    if (!row) {
      return;
    }

    const rowIndex = this.rows?.indexOf(row);
    if (this.isRowSaveDisabled(row, rowIndex)) {
      return;
    }

    if (!this.data?.saveUrl || !this.data?.saveFunction || (!this.data?.saveKey && !this.data?.savePayloadConfig)) {
      this.snackBar.open('Save API is not configured yet.', 'Close', {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error'],
      });
      return;
    }

    const { row_status, ...rowPayload } = row;
    rowPayload.__row_status = row_status;
    if (!this.validateMandatoryRows([rowPayload])) {
      return;
    }
    if (!this.validateDateRangeRows([rowPayload])) {
      return;
    }

    const confirmation = this.getRowSaveConfirmation(row, rowIndex);
    if (confirmation) {
      const dialogRef = this.dialog.open(ConfirmDialogueComponent, {
        width: confirmation.width || '420px',
        disableClose: true,
        data: {
          title: confirmation.title || 'Confirm Save',
          desc: confirmation.message || 'Are you sure you want to save this change?',
          noLabel: confirmation.noLabel || 'No',
          yesLabel: confirmation.yesLabel || 'Yes',
          showContinueText: false,
          showCloseButton: true,
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.saveRowAfterConfirmation(row, rowPayload, confirmation);
        }
      });
      return;
    }

    this.saveRowAfterConfirmation(row, rowPayload);
  }

  private getRowSaveConfirmation(row: any, rowIndex: number): any | null {
    const confirmation = this.data?.rowSaveConfirmation;
    if (!confirmation || (confirmation.when && !this.doesRowMatchRule(row, confirmation.when))) {
      return null;
    }

    const changedProp = confirmation.changedProp;
    const savedRow = this.findSavedRowSnapshot(row, rowIndex);
    if (!changedProp || !savedRow) {
      return null;
    }

    const currentDate = this.parseDateOnly(row?.[changedProp]);
    const savedDate = this.parseDateOnly(savedRow?.[changedProp]);
    if (!currentDate || !savedDate) {
      return null;
    }

    const isChanged = currentDate.getTime() !== savedDate.getTime();
    const isRequiredChange = confirmation.changeType === 'increase'
      ? currentDate.getTime() > savedDate.getTime()
      : isChanged;

    return isRequiredChange ? confirmation : null;
  }

  private saveRowAfterConfirmation(
    row: any,
    rowPayload: any,
    confirmation?: any
  ): void {
    this.isLoading = true;
    this.configService
      .saveData([rowPayload], this.filters?.source?.[0], this.data, this.email)
      .subscribe({
        next: (res: any) => {
          const commitStatus = this.getCommitStatus(res);
          if (commitStatus && commitStatus !== 'COMMIT_SUCCESS') {
            const errorStack = this.getErrorStackMessage(res);
            this.isLoading = false;
            this.snackBar.open(errorStack || `Failed to Save: Commit Status ${commitStatus}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
            return;
          }

          this.applySaveResponseToRow(row, res);
          this.runPostSaveActions(rowPayload, () => {
            row.row_status = 'unchanged';
            this.originalRows = this.cloneRows(this.rows);
            this.changed = this.rows.some((item: any) =>
              ['edited', 'new', 'deleted'].includes(String(item?.row_status || ''))
            );
            this.note = this.changed ? this.note : '';
            this.isLoading = false;
            if (confirmation?.successDialog) {
              const successDialog = confirmation.successDialog;
              this.dialog.open(ConfirmDialogueComponent, {
                width: successDialog.width || confirmation.width || '420px',
                data: {
                  title: successDialog.title || 'Extension Complete',
                  desc:
                    successDialog.message ||
                    'Extension mail has been sent successfully.',
                  yesLabel: successDialog.okLabel || 'OK',
                  hideNoButton: true,
                  showContinueText: false,
                  showCloseButton: true,
                },
              });
              return;
            }

            this.snackBar.open('Record updated successfully!', 'Close', {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-success'],
            });
          });
        },
        error: (err) => {
          console.error('Save failed:', err);
          this.isLoading = false;
          const errorStack = this.getErrorStackMessage(err?.error || err);
          this.snackBar.open(errorStack || 'Failed to save.', 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  private validateMandatoryRows(rows: any[]): boolean {
    const mandatoryColumns = (this.data?.headers || []).filter(
      (column: any) => this.hasMandatoryRule(column)
    );

    if (mandatoryColumns.length === 0) {
      return true;
    }

    const missingMandatoryColumnNames = new Set<string>();

    rows.forEach((row: any) => {
      if (row.is_deleted) return;

      mandatoryColumns.forEach((column: any) => {
        const value = row[column.prop];
        if (this.isColumnMandatoryForRow(column, row) && this.isBlank(value)) {
          missingMandatoryColumnNames.add(column.name || column.prop);
        }
      });
    });

    if (missingMandatoryColumnNames.size === 0) {
      return true;
    }

    const missingFields = Array.from(missingMandatoryColumnNames);
    const fieldNamesList = missingFields.map((name) => `- ${name}`).join('\n');
    this.snackBar.open(
      `Please fill mandatory field(s):\n${fieldNamesList}`,
      'Close',
      {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error', 'snackbar-multiline'],
      }
    );
    return false;
  }

  private validateDateRangeRows(rows: any[]): boolean {
    const constraints = this.getDateRangeConstraints();
    if (constraints.length === 0) {
      return true;
    }

    const invalidMessages = new Set<string>();

    rows.forEach((row: any) => {
      if (row.is_deleted) return;

      constraints.forEach((constraint: any) => {
        const startProp = constraint.startDateProp || 'start_date';
        const endProp = constraint.endDateProp || 'end_date';
        const startDate = this.parseDateOnly(row?.[startProp]);
        const endDate = this.parseDateOnly(row?.[endProp]);
        if (!startDate || !endDate) return;

        const minEndDate = this.addDays(startDate, Number(constraint.minEndOffsetDays ?? 1));
        if (endDate.getTime() < minEndDate.getTime()) {
          invalidMessages.add(
            constraint.message || 'End Date should be after Start Date.'
          );
        }
      });
    });

    if (invalidMessages.size === 0) {
      return true;
    }

    this.snackBar.open(Array.from(invalidMessages).join('\n'), 'Close', {
      duration: 5000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['snackbar-error', 'snackbar-multiline'],
    });
    return false;
  }

  private shouldUseLocalRows(): boolean {
    return this.data?.localOnly === true || Array.isArray(this.data?.staticRows);
  }

  private extractRowsFromResponse(response: any, primaryPayload: any): any[] {
    const responseData = response?.responseList?.[0]?.responseData;
    const candidates = this.data?.respKey
      ? [
          primaryPayload?.[this.data.respKey],
          response?.responseList?.[0]?.[this.data.respKey],
          response?.[this.data.respKey],
          responseData,
          response?.responseData,
          response,
        ]
      : [
          responseData,
          response?.responseData,
          primaryPayload,
          response,
        ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private normalizeLoadedRows(rows: any): any[] {
    const loadedRows = Array.isArray(rows) ? rows : [];

    return loadedRows.map((r: any) => {
      const updatedRow = { ...r, row_status: 'unchanged' };

      (this.data?.headers || []).forEach((col: any) => {
        if (
          col?.requestProp &&
          updatedRow[col.prop] === undefined &&
          updatedRow[col.requestProp] !== undefined
        ) {
          updatedRow[col.prop] = updatedRow[col.requestProp];
        }

        if (col?.loadValueMap && updatedRow[col.prop] !== undefined) {
          const rawValue = String(updatedRow[col.prop]);
          updatedRow[col.prop] = col.loadValueMap[rawValue] ?? updatedRow[col.prop];
        }

        if (col?.type === 'date') {
          updatedRow[col.prop] = this.normalizeDateCellValue(updatedRow[col.prop], col);
        }

        if (col.isMultiSelect) {
          const val = updatedRow[col.prop];
          updatedRow[col.prop] = this.normalizeMultiSelectValue(val);
          return;
        }

        if (
          col?.type === 'number' &&
          String(col?.prop || '').toLowerCase().includes('investment')
        ) {
          const rawVal = updatedRow[col.prop];
          if (rawVal === '' || rawVal === null || rawVal === undefined) {
            return;
          }

          const numericValue = Number(rawVal);
          if (Number.isFinite(numericValue)) {
            updatedRow[col.prop] = this.roundToMaxDecimals(numericValue, 4);
          }
        }
      });

      this.applyDerivedRowValues(updatedRow);
      return updatedRow;
    });
  }

  private applyDerivedRowValues(row: any, changedProp?: string): void {
    this.applyDateRangeStatus(row, changedProp);
  }

  private applyDateRangeStatus(row: any, changedProp?: string): void {
    const config = this.data?.derivedStatus;
    if (config?.type !== 'dateRange') {
      return;
    }

    const startProp = config.startDateProp || 'start_date';
    const endProp = config.endDateProp || 'end_date';
    const statusProp = config.statusProp || 'status';
    if (
      changedProp &&
      ![startProp, endProp, statusProp].includes(changedProp)
    ) {
      return;
    }

    const startDate = this.parseDateOnly(row?.[startProp]);
    const endDate = this.parseDateOnly(row?.[endProp]);
    if (!startDate || !endDate) {
      row[statusProp] = config.defaultStatus || 'Draft';
      return;
    }

    const today = this.toDateOnly(new Date());
    if (endDate.getTime() < today.getTime()) {
      row[statusProp] = config.completedStatus || 'Published';
      return;
    }

    if (
      startDate.getTime() <= today.getTime() &&
      today.getTime() <= endDate.getTime()
    ) {
      row[statusProp] = config.activeStatus || 'Open';
      return;
    }

    row[statusProp] = config.defaultStatus || 'Draft';
  }

  private parseDateOnly(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return this.toDateOnly(value);
    }

    if (typeof value === 'string') {
      const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnly) {
        return this.createLocalDateFromParts(dateOnly[1], dateOnly[2], dateOnly[3]);
      }
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : this.toDateOnly(parsed);
  }

  private normalizeDateCellValue(value: any, column?: any): any {
    if (!value || value instanceof Date) {
      return value;
    }

    if (typeof value === 'string') {
      if (column?.ignoreTimezone === true) {
        const apiDate = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
        if (apiDate) {
          return this.formatDateOnlyString(
            Number(apiDate[1]),
            Number(apiDate[2]),
            Number(apiDate[3])
          );
        }
      }

      const configuredOffset = this.getDateColumnTimezoneOffset(column);
      const configuredOffsetDate = value.match(
        new RegExp(`^(\\d{4})-(\\d{2})-(\\d{2})T.*${this.escapeRegExp(configuredOffset)}$`, 'i')
      );
      if (configuredOffsetDate) {
        return this.formatDateOnlyString(
          Number(configuredOffsetDate[1]),
          Number(configuredOffsetDate[2]),
          Number(configuredOffsetDate[3])
        );
      }

      const hasTimeZone = /T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
      if (hasTimeZone) {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          const offsetMinutes = this.getDateColumnTimezoneOffsetMinutes(column);
          const shifted = new Date(parsed.getTime() + offsetMinutes * 60 * 1000);
          return this.formatDateOnlyString(
            shifted.getUTCFullYear(),
            shifted.getUTCMonth() + 1,
            shifted.getUTCDate()
          );
        }
      }

      const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnly) {
        return this.formatDateOnlyString(
          Number(dateOnly[1]),
          Number(dateOnly[2]),
          Number(dateOnly[3])
        );
      }
    }

    return value;
  }

  formatDateCellValue(value: any, column?: any): string {
    const normalized = this.normalizeDateCellValue(value, column);
    const date = this.parseDateOnly(normalized);
    if (!date) {
      return '';
    }

    const pad = (part: number) => String(part).padStart(2, '0');
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
  }

  private createLocalDateFromParts(yearValue: string, monthValue: string, dayValue: string): Date | null {
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    if (![year, month, day].every(Number.isFinite)) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private formatDateOnlyString(year: number, month: number, day: number): string {
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}`;
  }

  private getDateColumnTimezoneOffsetMinutes(column?: any): number {
    const offset = this.getDateColumnTimezoneOffset(column);
    const match = String(offset).match(/^([+-])(\d{2}):(\d{2})$/);
    if (!match) {
      return 0;
    }

    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number(match[2]) * 60 + Number(match[3]));
  }

  private getDateColumnTimezoneOffset(column?: any): string {
    return String(
      column?.timezoneOffset ||
      this.data?.savePayloadConfig?.timezoneOffset ||
      '+00:00'
    );
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private toDateOnly(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  getDateInputMin(row: any, column: any): string | null {
    if (column?.type !== 'date') return null;
    const rangeMin = this.getDateRangeInputMin(row, column);
    const configuredMin = this.getConfiguredDateInputMin(column);
    const year = this.getDateConstraintYear(row, column);
    const yearMin = year ? `${year}-01-01` : null;
    return this.getLaterDateInputValue(
      this.getLaterDateInputValue(rangeMin, yearMin),
      configuredMin
    );
  }

  getDateInputMax(row: any, column: any): string | null {
    if (column?.type !== 'date') return null;
    const year = this.getDateConstraintYear(row, column);
    return year ? `${year}-12-31` : null;
  }

  private getDateConstraintYear(row: any, column: any): number | null {
    const config = this.data?.dateYearConstraint;
    if (!config) return null;

    const dateProps = Array.isArray(config.dateProps) ? config.dateProps : [];
    if (!dateProps.includes(column?.prop)) return null;

    const rawYear = row?.[config.yearProp || 'year'];
    const year = Number(rawYear?.value ?? rawYear);
    return Number.isInteger(year) && year > 0 ? year : null;
  }

  private applyYearDateConstraints(rowIndex: number, changedProp: string): void {
    const config = this.data?.dateYearConstraint;
    if (!config || changedProp !== (config.yearProp || 'year')) return;

    const row = this.rows[rowIndex];
    const year = Number(row?.[changedProp]?.value ?? row?.[changedProp]);
    if (!row || !Number.isInteger(year)) return;

    const dateProps = Array.isArray(config.dateProps) ? config.dateProps : [];
    dateProps.forEach((dateProp: string) => {
      const date = this.parseDateOnly(row[dateProp]);
      if (date && date.getFullYear() !== year) {
        row[dateProp] = null;
      }
    });
  }

  private getDateRangeConstraints(): any[] {
    const config = this.data?.dateRangeConstraints || this.data?.dateRangeConstraint;
    if (!config) return [];
    return Array.isArray(config) ? config : [config];
  }

  private getDateRangeInputMin(row: any, column: any): string | null {
    const constraint = this.getDateRangeConstraints().find(
      (item: any) => (item.endDateProp || 'end_date') === column?.prop
    );
    if (!constraint) return null;

    const startDate = this.parseDateOnly(row?.[constraint.startDateProp || 'start_date']);
    if (!startDate) return null;

    const minEndDate = this.addDays(startDate, Number(constraint.minEndOffsetDays ?? 1));
    return this.formatDateInputValue(minEndDate);
  }

  private getConfiguredDateInputMin(column: any): string | null {
    if (!column?.minDate) return null;

    if (column.minDate === 'today') {
      return this.formatDateInputValue(this.toDateOnly(new Date()));
    }

    const configuredDate = this.parseDateOnly(column.minDate);
    return configuredDate ? this.formatDateInputValue(configuredDate) : null;
  }

  private getLaterDateInputValue(first: string | null, second: string | null): string | null {
    if (!first) return second;
    if (!second) return first;

    const firstDate = this.parseDateOnly(first);
    const secondDate = this.parseDateOnly(second);
    if (!firstDate) return second;
    if (!secondDate) return first;

    return firstDate.getTime() >= secondDate.getTime() ? first : second;
  }

  private applyDateRangeConstraints(rowIndex: number, changedProp: string): void {
    const row = this.rows[rowIndex];
    if (!row) return;

    this.getDateRangeConstraints().forEach((constraint: any) => {
      const startProp = constraint.startDateProp || 'start_date';
      const endProp = constraint.endDateProp || 'end_date';
      if (![startProp, endProp].includes(changedProp)) return;

      const startDate = this.parseDateOnly(row[startProp]);
      const endDate = this.parseDateOnly(row[endProp]);
      if (!startDate || !endDate) return;

      const minEndDate = this.addDays(startDate, Number(constraint.minEndOffsetDays ?? 1));
      if (endDate.getTime() < minEndDate.getTime()) {
        row[endProp] = null;
      }
    });
  }

  private applyDateInputConstraints(rowIndex: number, changedProp: string): void {
    const row = this.rows[rowIndex];
    const column = this.getColumnByProp(changedProp);
    if (!row || column?.type !== 'date') return;

    const value = this.parseDateOnly(row[changedProp]);
    if (!value) return;

    const minValue = this.getDateInputMin(row, column);
    const minDate = minValue ? this.parseDateOnly(minValue) : null;
    if (minDate && value.getTime() < minDate.getTime()) {
      row[changedProp] = null;
      return;
    }

    const maxValue = this.getDateInputMax(row, column);
    const maxDate = maxValue ? this.parseDateOnly(maxValue) : null;
    if (maxDate && value.getTime() > maxDate.getTime()) {
      row[changedProp] = null;
    }
  }

  private addDays(value: Date, days: number): Date {
    const nextDate = this.toDateOnly(value);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  }

  private formatDateInputValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private saveAfterEditAccessCheck(): void {
    if (!this.data?.saveUrl || !this.data?.saveFunction || (!this.data?.saveKey && !this.data?.savePayloadConfig)) {
      this.snackBar.open('Save API is not configured yet.', 'Close', {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error'],
      });
      this.isLoading = false;
      return;
    }

    let changedRows = this.rows.filter(
      (row: any) =>
        row.row_status === 'edited' ||
        row.row_status === 'new' ||
        row.row_status === 'deleted'
    );

    if (!changedRows || changedRows.length === 0) {
      this.snackBar.open('No changes to save.', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
      this.isLoading = false; // Also stop isLoading here
      return;
    }

    const modifiedCount = changedRows.filter(
      (row: any) => row.row_status === 'edited' || row.row_status === 'new'
    ).length;
    const deletedCount = changedRows.filter(
      (row: any) => row.row_status === 'deleted' || row.is_deleted
    ).length;

    if (deletedCount > 0) {
      const proceed = globalThis.confirm(
        `You are modifying ${modifiedCount} records, deleting ${deletedCount} records.\nPlease click OK to proceed.`
      );

      if (!proceed) {
        return;
      }
    }

    this.isLoading = true;

    const mandatoryColumns = (this.data?.headers || []).filter(
      (column: any) => this.hasMandatoryRule(column)
    );

    if (mandatoryColumns.length > 0) {
      const missingMandatoryColumnNames = new Set<string>();

      changedRows.forEach((row: any) => {
        if (row.is_deleted) return;

        mandatoryColumns.forEach((column: any) => {
          const value = row[column.prop];
          if (this.isColumnMandatoryForRow(column, row) && this.isBlank(value)) {
            missingMandatoryColumnNames.add(column.name || column.prop);
          }
        });
      });

      if (missingMandatoryColumnNames.size > 0) {
        const missingFields = Array.from(missingMandatoryColumnNames);
        const fieldNamesList = missingFields.map((name) => `• ${name}`).join('\n');
        this.isLoading = false;
        this.snackBar.open(
          `Please fill mandatory field(s):\n${fieldNamesList}`,
          'Close',
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error', 'snackbar-multiline'],
          }
        );
        return;
      }
    }

    if (!this.validateDateRangeRows(changedRows)) {
      this.isLoading = false;
      return;
    }

    const numberColumnProps = new Set<string>(
      (this.data?.headers || [])
        .filter((column: any) => column?.type === 'number')
        .map((column: any) => column.prop)
    );
    const requestPropMappings = (this.data?.headers || [])
      .filter(
        (column: any) =>
          column?.prop &&
          column?.requestProp &&
          column.requestProp !== column.prop
      )
      .map((column: any) => ({
        prop: column.prop,
        requestProp: column.requestProp,
      }));
    const requestExcludedProps = new Set<string>(
      (this.data?.headers || [])
        .filter((column: any) => column?.prop && column?.excludeFromRequest)
        .map((column: any) => column.prop)
    );

    // Remove row_status and normalize configured number fields before sending.
    changedRows = changedRows.map(({ row_status, ...rest }) => {
      const normalizedRow: any = { ...rest };
      normalizedRow.__row_status = row_status;

      numberColumnProps.forEach((prop) => {
        if (!(prop in normalizedRow)) return;

        const value = normalizedRow[prop];
        if (value === '' || value === null || value === undefined) return;

        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
          normalizedRow[prop] = this.isIntegerColumn(prop)
            ? Math.trunc(numericValue)
            : Number(numericValue.toFixed(4));
        }
      });

      requestPropMappings.forEach(({ prop, requestProp }: any) => {
        if (!(prop in normalizedRow)) return;
        normalizedRow[requestProp] = normalizedRow[prop];
        delete normalizedRow[prop];
      });

      requestExcludedProps.forEach((prop) => {
        delete normalizedRow[prop];
      });

      return normalizedRow;
    });

    this.configService
      .saveData(changedRows, this.filters?.source?.[0], this.data, this.email)
      .subscribe({
        next: (res: any) => {
          const commitStatus = this.getCommitStatus(res);
          if (commitStatus !== 'COMMIT_SUCCESS') {
            const errorStack = this.getErrorStackMessage(res);
            this.isLoading = false;
            this.snackBar.open(
              errorStack ||
                `Failed to Save: Commit Status ${commitStatus || 'UNKNOWN'}`,
              'Close',
              {
                duration: 5000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['snackbar-error'],
              }
            );
            return;
          }

          this.runPostSaveActions(changedRows, (postSaveResponses) => {
            this.snackBar.open('Records updated successfully!', 'Close', {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-success'],
            });
            this.changed = false;
            this.note = '';
            const refreshResponse = postSaveResponses?.[postSaveResponses.length - 1];
            if (refreshResponse) {
              this.applyRowsResponse(refreshResponse);
            } else {
              this.getRows();
            }
          });
        },
        error: (err) => {
          console.error('Save failed:', err);
          this.isLoading = false;
          const errorStack = this.getErrorStackMessage(err?.error || err);
          this.snackBar.open(errorStack || 'Failed to save.', 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  private getCommitStatus(response: any): string {
    const status =
      response?.commitStatus ??
      response?.data?.commitStatus ??
      response?.responseList?.[0]?.commitStatus ??
      response?.responseList?.[0]?.data?.commitStatus;

    return String(status || '').toUpperCase();
  }

  private applySaveResponseToRow(row: any, response: any): void {
    const entity = this.data?.savePayloadConfig?.entity || response?.entity;
    const savedData = entity
      ? response?.responseList?.[0]?.[entity]?.[0]?.data
      : null;

    if (!savedData || typeof savedData !== 'object') {
      return;
    }

    (this.data?.headers || []).forEach((column: any) => {
      const responseProp = column?.requestProp || column?.prop;
      if (!column?.prop || savedData[responseProp] === undefined) {
        return;
      }

      let value = savedData[responseProp];

      // Some save endpoints return null date fields even though the submitted
      // dates were persisted. Do not erase the user's date until the follow-up
      // read supplies the canonical value.
      if (column?.type === 'date' && this.isBlank(value) && !this.isBlank(row[column.prop])) {
        return;
      }

      if (column?.loadValueMap && value !== null) {
        value = column.loadValueMap[String(value)] ?? value;
      }

      if (column?.type === 'date') {
        value = this.normalizeDateCellValue(value, column);
      }

      row[column.prop] = value;
    });
  }

  private runPostSaveActions(savedRows: any, onComplete: (responses?: any[]) => void): void {
    const actions = Array.isArray(this.data?.postSaveActions)
      ? this.data.postSaveActions
      : [];
    if (!actions.length) {
      onComplete();
      return;
    }

    const rows = Array.isArray(savedRows) ? savedRows : [savedRows];
    const contextRow = rows[0] || {};
    const responses: any[] = [];
    let remaining = actions.length;
    let failed = false;

    const finishOne = () => {
      remaining -= 1;
      if (remaining <= 0 && !failed) {
        onComplete(responses);
      }
    };

    actions.forEach((action: any) => {
      const payload = this.replacePayloadTokens(
        action.payload,
        this.buildPayloadTokenReplacements(contextRow)
      );

      this.configService
        .customApi(action.url || this.data?.saveUrl || this.data?.apiUrl, payload, this.data, action.method || 'post')
        .subscribe({
          next: (response: any) => {
            responses.push(response);
            finishOne();
          },
          error: (err: any) => {
            failed = true;
            console.error('Post-save action failed:', err);
            this.isLoading = false;
            this.snackBar.open(
              err?.error?.message || action.errorMessage || 'Saved, but follow-up API failed.',
              'Close',
              {
                duration: 5000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['snackbar-error'],
              }
            );
          },
        });
    });
  }

  private buildPayloadTokenReplacements(row: any): Record<string, any> {
    const currentYear = new Date().getFullYear();
    const loginUserEmail = this.getCurrentUserEmail();
    const loginUserEmailPrefix = String(loginUserEmail || '').split('@')[0];
    const timezoneOffset =
      this.data?.savePayloadConfig?.timezoneOffset ||
      this.data?.rowActionPayloadConfig?.timezoneOffset ||
      '+05:30';
    const replacements: Record<string, any> = {
      LOGIN_USER_EMAIL: loginUserEmail,
      LOGIN_USER_EMAIL_PREFIX: loginUserEmailPrefix,
      USER_EMAIL_PREFIX: loginUserEmailPrefix,
      CURRENT_YEAR: currentYear,
      YEAR: currentYear,
    };

    Object.entries(row || {}).forEach(([key, value]) => {
      replacements[String(key).toUpperCase()] = value;
      replacements[`ROW_${String(key).toUpperCase()}`] = value;
    });

    const startDate = row?.start_date ?? row?.startDate;
    const endDate = row?.end_date ?? row?.endDate;
    replacements['START_DATE_ISO_OFFSET'] = this.formatPayloadDateWithOffset(
      startDate,
      timezoneOffset
    );
    replacements['END_DATE_ISO_OFFSET'] = this.formatPayloadDateWithOffset(
      endDate,
      timezoneOffset
    );

    return replacements;
  }

  private formatPayloadDateWithOffset(value: any, offset: string): any {
    if (!value) {
      return value;
    }

    if (typeof value === 'string') {
      const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnly) {
        return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}T00:00:00${offset}`;
      }

      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return value;
      }
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T00:00:00${offset}`;
  }

  private getErrorStackMessage(response: any): string {
    const candidates = [
      response?.ErrorStack,
      response?.errorStack,
      response?.error,
      response?.data?.ErrorStack,
      response?.data?.errorStack,
      response?.data?.error,
      response?.responseList?.[0]?.ErrorStack,
      response?.responseList?.[0]?.errorStack,
      response?.responseList?.[0],
      response?.responseList?.[0]?.data?.ErrorStack,
      response?.responseList?.[0]?.data?.errorStack,
      response?.responseList?.[0]?.data?.error,
      response?.message,
      response?.error?.message,
    ];

    const message = candidates.find(
      (item) => typeof item === 'string' && item.trim() !== ''
    );
    const normalized = String(message || '').trim();
    if (!normalized) {
      return '';
    }

    const marker = 'ErrorStack -';
    const markerIndex = normalized.indexOf(marker);
    if (markerIndex >= 0) {
      return normalized.slice(markerIndex).trim();
    }

    const sqlErrorMarker = 'ERROR:';
    const sqlErrorIndex = normalized.indexOf(sqlErrorMarker);
    if (sqlErrorIndex >= 0) {
      return normalized
        .slice(sqlErrorIndex)
        .replace(/\\n/g, '\n')
        .trim();
    }

    return normalized;
  }

  refresh() {
    // Keep existing columnFilters so reload does not reset user-applied filters.
    this.clearUiCaches();
    this.searchText = '';
    this.loadAndRenderChart(this.filters);
  }

  private scheduleTableRecalculate(): void {
    if (!this.table) {
      return;
    }

    setTimeout(() => {
      this.table?.recalculate?.();
      this.table?.bodyComponent?.updateRows?.();
    });
  }
  ngOnDestroy(): void {
    this.stopCacheReloadTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  downloadCSV() {
    let filePath = '/assets/ecosystem.csv'; // Adjust path as needed
    const source = this.filters?.source?.[0]?.toLowerCase();
    switch (source) {
      case 'vpcr':
        filePath = '/assets/downloadFiles/[vpcr]ecosystem.csv';
        break;
      case 'vpi':
        filePath = '/assets/downloadFiles/ecosystem.csv';
        break;
      case 'pps/implementation':
        filePath = '/assets/downloadFiles/[pps]ecosystem.csv';
        break;
      case 'fap':
        filePath = '/assets/downloadFiles/ecosystem.csv';
        break;
      default:
        filePath = '/assets/downloadFiles/ecosystem.csv';
        break;
    }
    const link = document.createElement('a');
    link.href = filePath;
    link.click();
  }

  downloadData() {
    this.isLoading = true;
    const filters = { ...this.filters, ...this.columnFilters };
    if (this.data.downloadRequestKey) {
      this.configService
        .fetchData2(
          this.data.downloadDataUrl,
          this.data.downloadFunctionName,
          filters,
          this.data,
          'post',
          { responseType: 'blob' as 'json', observe: 'response' as 'body' }
        )
        .subscribe({
          next: (response: any) => {
            const res: Blob = response.body; // The blob data

            //extract filename from headers
            let filename = 'exported_data.xlsx';
            const contentDisposition = response.headers?.get(
              'Content-Disposition'
            );
            if (contentDisposition) {
              const match = contentDisposition.match(/filename="?(.+)"?/);
              if (match && match[1]) filename = match[1];
            }

            // Create download link
            const blob = new Blob([res], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.isLoading = false
          },
          error: ((err) => {
            this.isLoading = false;
            console.error('Download error:', err);
          })
        });
    }
    else {
      this.configService
        .fetchData(
          this.data.downloadDataUrl,
          this.data.downloadFunctionName,
          filters,
          this.data,
          'post',
          { responseType: 'blob' as 'json', observe: 'response' as 'body' }
        )
        .subscribe({
          next: (response: any) => {
            const res: Blob = response.body; // The blob data

            //extract filename from headers
            let filename = 'exported_data.xlsx';
            const contentDisposition = response.headers?.get(
              'Content-Disposition'
            );
            if (contentDisposition) {
              const match = contentDisposition.match(/filename="?(.+)"?/);
              if (match && match[1]) filename = match[1];
            }

            // Create download link
            const blob = new Blob([res], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.isLoading = false
          },
          error: ((err) => {
            this.isLoading = false;
            console.error('Download error:', err);
          })
        });
    }
  }

  downloadExcel() {
    this.isLoading = true;

    const filters = { ...this.filters, ...this.columnFilters };
    const downloadExcelFunctionName =
      this.resolveDownloadExcelFunctionName(filters);
    const exportColumns = this.buildExportColumns();

    this.configService.downloadExcel(
      this.data.downloadExcelDataUrl,
      downloadExcelFunctionName,
      filters,
      this.data,
      this.email,
      exportColumns
    ).subscribe({
      next: (data: any) => {
        this.isLoading = false;

        if (data?.data?.fileContent) {
          try {
            let base64 = data.data.fileContent;

            //  Debug logs (optional – can remove later)
            console.log('fileContent sample:', base64.substring(0, 100));
            console.log('fileContent length:', base64.length);

            //  Normalize base64
            base64 = base64.replace(/\s/g, '');
            const padding = base64.length % 4;
            if (padding) {
              base64 += '='.repeat(4 - padding);
            }

            // Decode base64
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const blob = new Blob([new Uint8Array(byteNumbers)], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            //  Filename fallback
            const filename = data.data.fileName || 'exported_data.xlsx';

            //  Trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

          } catch (e) {
            console.error('Error processing base64 file content:', e);
            this.snackBar.open('Failed to decode downloaded file.', 'Close', {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
          }
        } else {
          console.error('Invalid download response:', data);
          this.snackBar.open(
            'Download failed: Response did not contain file content.',
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            }
          );
        }
      },

      error: (err: any) => {
        this.isLoading = false;
        console.error('Download error:', err);
        this.snackBar.open('Download failed.', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-error'],
        });
      }
    });
  }

  enableEditAccess(): void {
    const action = this.data?.editAccessAction;
    if (!action?.url || !action?.payload) {
      this.snackBar.open('Edit access action is not configured.', 'Close', {
        duration: 4000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error'],
      });
      return;
    }

    const currentEditEnabled = this.getCurrentEditAccessEnabled();
    const dialogRef = this.dialog.open(ConfirmDialogueComponent, {
      width: action.confirmWidth || '420px',
      disableClose: true,
      data: {
        title: action.confirmTitle || 'Edit Access',
        desc: currentEditEnabled
          ? action.lockOnMessage || 'Lock will disable edit access.'
          : action.lockOffMessage || 'Unlock will enable edit access.',
        productIds: [],
        noLabel: action.lockOnLabel || 'Lock',
        yesLabel: action.lockOffLabel || 'Unlock',
        hideNoButton: !currentEditEnabled,
        hideYesButton: currentEditEnabled,
        showCloseButton: true,
        showContinueText: false,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed !== true && confirmed !== false) {
        return;
      }

      const isEditEnabled = confirmed === true;
      const payload = this.replacePayloadTokens(action.payload, {
        LOGIN_USER_EMAIL: this.getCurrentUserEmail(),
        PROJECT_CATEGORY: this.resolveEditAccessProjectCategory(action),
        PLANNING_YEAR: this.resolveEditAccessPlanningYear(action),
      });
      this.applyEditAccessBusinessKey(payload, action);
      this.setPayloadValue(
        payload,
        action.editEnabledPath || 'requestData.is_edit_enabled',
        isEditEnabled
      );
      for (const path of action.omitPayloadPaths || []) {
        this.deletePayloadPath(payload, path);
      }

      this.isLoading = true;
      this.configService.customApi(action.url, payload, this.data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          const responseKey =
            this.data.editAccessControl?.responseKey || 'is_edit_enabled';
          this.latestEditAccessResponse = { [responseKey]: isEditEnabled };
          this.cacheEditAccessState(isEditEnabled);
          if (!isEditEnabled) {
            this.exitEditAfterAccessLocked();
          }
          this.snackBar.open(
            response?.message ||
              (isEditEnabled
                ? action.successMessage || 'Edit access unlocked.'
                : action.lockSuccessMessage || 'Edit access locked.'),
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
          this.refresh();
        },
        error: (err: any) => {
          this.isLoading = false;
          console.error('Edit access update failed:', err);
          this.snackBar.open(
            err?.error?.message || action.errorMessage || 'Failed to update edit access.',
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            }
          );
        },
      });
    });
  }

  private getCurrentUserEmail(): string {
    return (
      this.email ||
      this.keycloakService.getKeycloakInstance().idTokenParsed?.['email'] ||
      this.keycloakService.getKeycloakInstance().tokenParsed?.['email'] ||
      ''
    );
  }

  private resolveEditAccessProjectCategory(action: any): string {
    return String(
      action?.projectCategory ??
      this.findAdditionalFilterValue('project_category') ??
      this.findAdditionalFilterValue('copy_to') ??
      ''
    );
  }

  private resolveEditAccessPlanningYear(action: any): string {
    return String(
      action?.planningYear ??
      this.findAdditionalFilterValue(this.getPlanningYearKey()) ??
      ''
    );
  }

  private applyEditAccessBusinessKey(payload: any, action: any): void {
    const projectCategory = this.resolveEditAccessProjectCategory(action);
    const planningYear = this.resolveEditAccessPlanningYear(action);
    const businessKey = Array.isArray(action?.businessKey)
      ? action.businessKey
      : ['project_category', this.getPlanningYearKey()];

    if (projectCategory) {
      this.setPayloadValue(payload, 'requestData.project_category', projectCategory);
    }

    if (planningYear) {
      this.setPayloadValue(payload, `requestData.${this.getPlanningYearKey()}`, planningYear);
    }

    this.setPayloadValue(payload, 'requestData.businessKey', businessKey);
    this.setPayloadValue(payload, 'requestData.operation', action?.operation || 'update');
  }

  private findAdditionalFilterValue(key: string): any {
    const filters = Array.isArray(this.data?.additionalFilters)
      ? this.data.additionalFilters
      : [];

    for (const filter of filters) {
      if (!filter || typeof filter !== 'object' || !(key in filter)) {
        continue;
      }

      return this.unwrapFilterValue(filter[key]);
    }

    return null;
  }

  private unwrapFilterValue(value: any): any {
    if (Array.isArray(value)) {
      return this.unwrapFilterValue(value[0]);
    }

    return value;
  }

  private replacePayloadTokens(value: any, replacements: Record<string, any>): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.replacePayloadTokens(item, replacements));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce((acc: Record<string, any>, [key, item]) => {
        acc[key] = this.replacePayloadTokens(item, replacements);
        return acc;
      }, {});
    }

    if (typeof value !== 'string') {
      return value;
    }

    const exactToken = value.match(/^__([A-Za-z0-9_]+)__$/);
    if (exactToken) {
      return replacements[exactToken[1].toUpperCase()] ?? value;
    }

    return value.replace(/__([A-Za-z0-9_]+)__/g, (match, token) => {
      token = String(token).toUpperCase();
      return replacements[token] ?? match;
    });
  }

  private setPayloadValue(payload: any, path: string, value: any): void {
    const keys = String(path || '').split('.').filter(Boolean);
    if (!keys.length) return;

    let target = payload;
    for (let index = 0; index < keys.length - 1; index++) {
      const key = keys[index];
      if (!target[key] || typeof target[key] !== 'object') {
        target[key] = {};
      }
      target = target[key];
    }

    target[keys[keys.length - 1]] = value;
  }

  private deletePayloadPath(payload: any, path: string): void {
    const keys = String(path || '').split('.').filter(Boolean);
    if (!keys.length) return;

    let target = payload;
    for (const key of keys.slice(0, -1)) {
      if (!target || typeof target !== 'object') return;
      target = target[key];
    }

    if (target && typeof target === 'object') {
      delete target[keys[keys.length - 1]];
    }
  }

  private buildExportColumns(): Array<{ prop: string; name: string }> {
    const headers = Array.isArray(this.data?.headers) ? this.data.headers : [];

    return headers
      .filter((col: any) => !(col?.showOnEdit && !this.isEditMode))
      .map((col: any) => ({
        prop: String(col?.prop ?? '').trim(),
        name: String(col?.name ?? col?.prop ?? '').trim(),
      }))
      .filter((col: { prop: string; name: string }) => !!col.prop);
  }

  private resolveDownloadExcelFunctionName(filters: any): string {
    const baseFunctionName = String(this.data?.downloadExcelFunctionName || '');
    const supportedBaseNames = new Set([
      'SimPlanningExport',
      'AOPlanningExport',
      'IdeasHopperExport',
    ]);

    if (!supportedBaseNames.has(baseFunctionName)) {
      return baseFunctionName;
    }

    const sourceValue = filters?.source;
    const normalizedSource = Array.isArray(sourceValue)
      ? String(sourceValue[0] ?? '').trim().toUpperCase()
      : String(sourceValue ?? '').trim().toUpperCase();

    if (normalizedSource === 'VPI') {
      return `${baseFunctionName}_VPI`;
    }

    if (normalizedSource === 'FAP') {
      return `${baseFunctionName}_FAP`;
    }

    // Default includes ALL/VPCR/empty source.
    return baseFunctionName;
  }



  private _getFileExtension(filename: string): string {
    const index = filename.lastIndexOf('.');
    return index !== -1 ? filename.substring(index) : '';
  }

  private _handleUpload(uploadObservable: Observable<any>) {
    uploadObservable.subscribe({
      next: (res: any) => {
        this.snackBar.open(res?.message || 'Upload completed successfully.', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
        this.refresh();
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Upload error', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  upload() {
    const projectCategory = this.resolveUploadProjectCategory();
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { excel: false },
    });

    dialogRef.afterClosed().subscribe((file: File | null) => {
      if (!file) return;
      this.isLoading = true;
      const uploadServiceObservable = this.configService.upload(
        file,
        this.data.uploadUrl,
        projectCategory,
        this.email
      );
      this._handleUpload(uploadServiceObservable);
    });
  }

  uploadExcel() {
    const projectCategory = this.resolveUploadProjectCategory();
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { excel: true, projectCategory },
    });

    dialogRef.afterClosed().subscribe((file: File | null) => {
      if (!file) return;
      this.isLoading = true;
      const uploadServiceObservable = this.appConfigService.uploadexcel(
        file,
        this.data.uploadExcelUrl,
        projectCategory,
        this.email,
        this.data?.uploadExcelAction
      );
      this._handleUpload(uploadServiceObservable);
    });
  }

  private resolveUploadProjectCategory(): string {
    const filterCategory = this.filters?.project_category;
    if (Array.isArray(filterCategory)) {
      return String(filterCategory[0] ?? '').trim();
    }

    if (filterCategory !== undefined && filterCategory !== null) {
      return String(filterCategory).trim();
    }

    const configuredCategory = this.data?.uploadProjectCategory;
    if (configuredCategory !== undefined && configuredCategory !== null) {
      return String(configuredCategory).trim();
    }

    const additionalFilterCategory = (this.data?.additionalFilters || [])
      .find((filterItem: any) => filterItem?.project_category)?.project_category;

    if (Array.isArray(additionalFilterCategory)) {
      return String(additionalFilterCategory[0] ?? '').trim();
    }

    return additionalFilterCategory !== undefined && additionalFilterCategory !== null
      ? String(additionalFilterCategory).trim()
      : '';
  }

  onPinColumn(column: any, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!column?.prop) return;

    this.pinnedColumnProp =
      this.pinnedColumnProp === column.prop ? null : column.prop;
    this.headerClassCache = {};
    this.cellClassCache = {};

    if (this.table) {
      this.table.recalculate();
      this.table.bodyComponent?.updateRows();
    }
    this.cdr.detectChanges();
  }

  isColumnPinned(column: any): boolean {
    return !!column?.prop && this.pinnedColumnProp === column.prop;
  }

  private isColumnInPinnedPane(column: any): boolean {
    if (!this.pinnedColumnProp || !column?.prop || column.prop === 'delete') {
      return false;
    }

    const headers = this.data?.headers || [];
    const pinnedIndex = headers.findIndex(
      (header: any) => header?.prop === this.pinnedColumnProp
    );
    const columnIndex = headers.findIndex(
      (header: any) => header?.prop === column.prop
    );

    return pinnedIndex >= 0 && columnIndex >= 0 && columnIndex <= pinnedIndex;
  }

  isColumnFrozen(column: any, columnIndex: number): boolean {
    if (column?.prop === 'delete') {
      return false;
    }

    if (this.pinnedColumnProp) {
      const pinnedIndex = (this.data?.headers || []).findIndex(
        (header: any) => header?.prop === this.pinnedColumnProp
      );
      return pinnedIndex >= 0 && columnIndex <= pinnedIndex;
    }

    return column?.freeze === true || column?.frozenLeft === true;
  }

  private resetPinnedColumnState(): void {
    if (!this.pinnedColumnProp) return;

    const hasPinnedColumn = (this.data?.headers || []).some(
      (header: any) => header?.prop === this.pinnedColumnProp
    );

    if (!hasPinnedColumn) {
      this.pinnedColumnProp = null;
    }
  }

  getHeaderClass(column: any) {
    const cacheKey = `${String(column?.prop || column?.name || 'default')}:${this.pinnedColumnProp || ''}`;
    if (this.headerClassCache[cacheKey] !== undefined) {
      return this.headerClassCache[cacheKey];
    }

    const classes = [
      column.auto ? 'auto-header' : '',
      this.isColumnInPinnedPane(column) ? 'pinned-left-column' : '',
    ].filter(Boolean);
    const className = classes.join(' ');
    this.headerClassCache[cacheKey] = className;
    return className;
  }
  getCellClass(column: any) {
    const cacheKey = `${String(column?.prop || column?.name || 'default')}:${this.pinnedColumnProp || ''}`;
    if (this.cellClassCache[cacheKey] !== undefined) {
      return this.cellClassCache[cacheKey];
    }

    const requestedAlign = String(
      column?.text_align ||
        (column?.type === 'number' ? 'right' : 'left')
    ).toLowerCase();
    const align = ['left', 'center', 'right'].includes(requestedAlign)
      ? requestedAlign
      : 'left';
    const classes = [
      `align-${align}`,
      this.isColumnInPinnedPane(column) ? 'pinned-left-column' : '',
      this.isCommentTooltipColumn(column) ? 'comment-edit-cell' : '',
    ];
    const className = classes.join(' ');
    this.cellClassCache[cacheKey] = className;
    return className;
  }
  getColumnTotal(columnKey: string): number {
    const data =
      this.selectedRows && this.selectedRows.length > 0
        ? this.selectedRows
        : this.rows;
    if (data.length === 0) return 0;

    const total = data.reduce((sum: number, row: any) => {
      const value = parseFloat(row[columnKey]) || 0;
      return sum + value;
    }, 0);

    return this.roundToDecimals(total, 4);
  }

  applyFilters(event?: string) {
    const activeFilterKeys = Object.keys(this.columnFilters).filter((col) => {
      const selectedValues = this.columnFilters[col];
      return Array.isArray(selectedValues) && selectedValues.length > 0;
    });

    if (activeFilterKeys.length === 0) {
      this.rows = this.cloneRows(this.originalRows);
      this.applyCurrentSort();
      if (event === 'close') {
        this.closeMenu();
      }
      return;
    }

    this.rows = this.cloneRows(this.originalRows.filter((row) => {
      return activeFilterKeys.every((col) => {
        const selectedValues = this.columnFilters[col];

        const cellValue = row[col];

        // If the cell has multiple values (multi-select)
        if (Array.isArray(cellValue)) {
          // Check if any selected value matches any in cellValue
          return cellValue.some((v) => selectedValues.includes(v));
        }

        // For single values, simple includes check
        return selectedValues.includes(cellValue);
      });
    }));

    this.applyCurrentSort();

    if (event === 'close') {
      this.closeMenu();
    }
  }

  clearAllFilters() {
    Object.keys(this.columnFilters).forEach((key) => {
      this.columnFilters[key] = [];
    });
    this.searchText = '';
    this.uniqueValuesCache = {};
    this.dropdownVisibilityCache = {};
    this.rows = this.cloneRows(this.originalRows);
    this.applyFilters();
  }

  clearFilter(columnProp: string | undefined) {
    if (!columnProp) return;
    this.columnFilters[columnProp] = [];
    this.searchText = '';
    this.uniqueValuesCache = {};
    this.dropdownVisibilityCache = {};
    this.applyFilters();
    this.closeMenu();
  }

  onFilterChange(column: any, value: any, checked: boolean) {
    if (!this.columnFilters[column]) this.columnFilters[column] = [];
    if (checked) {
      this.columnFilters[column].push(value);
    } else {
      this.columnFilters[column] = this.columnFilters[column].filter(
        (v: any) => v !== value
      );
    }
  }

  searchText: string = '';
  uniqueValuesCache: { [key: string]: any[] } = {};
  activeColumn: any = null;

  filterSearch(columnProp: string | undefined, event: any): void {
    if (!columnProp) return;
    this.searchText = event.target.value || '';
  }

  getUniqueValues(columnProp: string | undefined): any[] {
    if (!columnProp) return [];

    if (!this.uniqueValuesCache[columnProp]) {
      // Flatten values safely, supporting arrays and primitives
      const allValues = this.originalRows.flatMap((row: any) => {
        const value = row[columnProp];

        if (Array.isArray(value)) {
          return value; // multi-select → expand values
        } else if (value !== null && value !== undefined) {
          return [value]; // single value
        } else {
          return []; // skip null/undefined
        }
      });

      // Deduplicate while keeping primitives clean
      this.uniqueValuesCache[columnProp] = Array.from(new Set(allValues));
    }

    const allUnique = this.uniqueValuesCache[columnProp];

    // Apply case-insensitive search if needed
    const searchText = this.searchText.trim().toLowerCase();
    if (!searchText) return allUnique;
    return allUnique.filter((v) =>
      (v ?? '').toString().toLowerCase().includes(searchText)
    );
  }

  isValueChecked(columnProp: string | undefined, value: any): boolean {
    if (!columnProp) return false;
    return this.columnFilters[columnProp]?.includes(value) ?? false;
  }

  activeTrigger: MatMenuTrigger | null = null;
  openFilter(column: any, trigger: MatMenuTrigger, event: MouseEvent) {
    event.stopPropagation();

    // Close any previously open filter menu
    if (this.activeTrigger && this.activeTrigger !== trigger) {
      this.activeTrigger.closeMenu();
    }

    this.activeColumn = column;
    this.activeTrigger = trigger;
    this.searchText = '';

    // Open the new menu
    trigger.openMenu();
  }

  closeMenu(event?: Event) {
    event?.stopPropagation();
    if (this.activeTrigger) {
      this.activeTrigger.closeMenu();
      this.activeTrigger = null;
    }
    this.activeColumn = null;
    this.searchText = '';
  }

  onSort(event: any) {
    const sort = event?.sorts?.[0];
    if (!sort?.prop) {
      return;
    }

    const prop = sort.prop;
    const dir: 'asc' | 'desc' = sort.dir === 'desc' ? 'desc' : 'asc';
    this.sortState = { prop, dir };

    this.rows = this.sortRows(this.rows, prop, dir);
    this.scheduleTableRecalculate();
  }

  /** Check if all values are selected */
  areAllSelected(columnProp: string | undefined): boolean {
    if (!columnProp) return false;
    const allValues = this.getUniqueValues(columnProp);
    const selectedValues = this.columnFilters[columnProp] || [];
    return (
      allValues.length > 0 && allValues.every((v) => selectedValues.includes(v))
    );
  }

  /** Check if partially selected */
  selectPerOne(columnProp: string | undefined): boolean {
    if (!columnProp) return false;
    const allValues = this.getUniqueValues(columnProp);
    const selectedValues = this.columnFilters[columnProp] || [];
    return (
      selectedValues.length > 0 && selectedValues.length < allValues.length
    );
  }

  /** Toggle all selections */
  toggleAll(columnProp: string | undefined, checked: boolean): void {
    if (!columnProp) return;
    const allValues = this.getUniqueValues(columnProp);

    if (checked) {
      // Select all visible (filtered) values
      this.columnFilters[columnProp] = [...allValues];
    } else {
      // Deselect all
      this.columnFilters[columnProp] = [];
    }
  }

  //Highlighting unmatched values in dropdown columns

  isBlank(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'string') return value.trim() === '';
    return false;
  }

  isValueInList(value: any, list: any[]): boolean {
    if (this.isBlank(value)) return true; // ignore blanks
    if (!Array.isArray(list)) return true;

    const normalizedValue = this.normalizeDropdownComparableValue(value);
    return list.some(
      (opt) => this.normalizeDropdownComparableValue(opt?.value ?? opt) === normalizedValue
    );
  }

  getUnmatchedValues(values: any, list: any[]): any[] {
    if (!Array.isArray(values)) return [];
    if (!Array.isArray(list)) return [];

    const validValues = new Set(
      list.map((opt) => this.normalizeDropdownComparableValue(opt?.value ?? opt))
    );
    return values.filter(
      (v) =>
        !this.isBlank(v) &&
        !validValues.has(this.normalizeDropdownComparableValue(v))
      );
  }

  compareDropdownValues = (first: any, second: any): boolean =>
    this.normalizeDropdownComparableValue(first) ===
    this.normalizeDropdownComparableValue(second);

  private normalizeDropdownComparableValue(value: any): string {
    const rawValue = value?.value ?? value;
    return String(rawValue ?? '').trim().toLowerCase();
  }

  shouldEnableDropdownSearch(column: any): boolean {
    return !!column?.enableSearch;
  }

  private getDropdownSearchKey(rowIndex: number, columnProp: string): string {
    return `${rowIndex}::${columnProp}`;
  }

  getDropdownSearchText(rowIndex: number, columnProp: string): string {
    return this.dropdownSearchText[this.getDropdownSearchKey(rowIndex, columnProp)] || '';
  }

  onDropdownSearchInput(rowIndex: number, columnProp: string, value: string): void {
    this.dropdownVisibilityCache = {};
    this.dropdownSearchText[this.getDropdownSearchKey(rowIndex, columnProp)] = value || '';
  }

  getFilteredDropdownOptions(column: any, rowIndex: number): any[] {
    const options = this.resolveDropdownOptions(column);
    if (!this.shouldEnableDropdownSearch(column) || !column?.prop) {
      return options;
    }

    const searchText = this.getDropdownSearchText(rowIndex, column.prop).trim().toLowerCase();

    if (!searchText) {
      return options;
    }

    return options.filter((option: any) => {
      const label = String(option?.viewValue ?? option ?? '')
        .trim()
        .toLowerCase();
      return label.includes(searchText);
    });
  }

  isDropdownOptionVisible(option: any, column: any, rowIndex: number): boolean {
    if (!this.shouldEnableDropdownSearch(column) || !column?.prop) {
      return true;
    }

    const searchText = this.getDropdownSearchText(rowIndex, column.prop).trim().toLowerCase();
    if (!searchText) {
      return true;
    }

    const optionKey = this.getDropdownOptionKey(option?.value ?? option);
    const visibilityCacheKey = `${rowIndex}::${column.prop}::${searchText}::${optionKey}`;
    const cachedVisibility = this.dropdownVisibilityCache[visibilityCacheKey];
    if (cachedVisibility !== undefined) {
      return cachedVisibility;
    }

    const label = String(option?.viewValue ?? option ?? '').trim().toLowerCase();
    const isVisible = label.includes(searchText);
    this.dropdownVisibilityCache[visibilityCacheKey] = isVisible;
    return isVisible;
  }

  getDropdownOptionsForDisplay(column: any, rowIndex: number): any[] {
    const options = this.resolveDropdownOptions(column);
    if (!column?.prop || options.length === 0) {
      return options;
    }

    const selectedValue = this.rows?.[rowIndex]?.[column.prop];
    const selectedValues = column.isMultiSelect
      ? this.normalizeMultiSelectValue(selectedValue)
      : this.isBlank(selectedValue)
        ? []
        : [selectedValue];

    if (selectedValues.length === 0) {
      return options;
    }

    const selectedSignature = selectedValues
      .map((value: any) => this.getDropdownOptionKey(value))
      .sort()
      .join('|');
    const displayCacheKey = `${rowIndex}::${column.prop}::${selectedSignature}`;
    const cachedDisplayOptions = this.dropdownDisplayCache[displayCacheKey];
    if (cachedDisplayOptions) {
      return cachedDisplayOptions;
    }

    const selectedKeys = new Set(
      selectedValues.map((value: any) => this.getDropdownOptionKey(value))
    );

    const selectedOptions: any[] = [];
    const unselectedOptions: any[] = [];

    options.forEach((option: any) => {
      const optionKey = this.getDropdownOptionKey(option?.value ?? option);
      if (selectedKeys.has(optionKey)) {
        selectedOptions.push(option);
      } else {
        unselectedOptions.push(option);
      }
    });

    const orderedOptions = [...selectedOptions, ...unselectedOptions];
    this.dropdownDisplayCache[displayCacheKey] = orderedOptions;
    return orderedOptions;
  }

  private resolveDropdownOptions(column: any): any[] {
    if (Array.isArray(column?.list)) {
      return column.list;
    }

    if (column?.dynamicList?.type === 'yearRange') {
      const currentYear = new Date().getFullYear();
      const startYear = Number.isFinite(Number(column.dynamicList.startYear))
        ? Number(column.dynamicList.startYear)
        : currentYear;
      const count = Number.isFinite(Number(column.dynamicList.count))
        ? Number(column.dynamicList.count)
        : 10;

      return Array.from({ length: Math.max(count, 0) }, (_value, index) => startYear + index);
    }

    return [];
  }

  trackByDropdownOption = (_index: number, option: any): string => {
    return this.getDropdownOptionKey(option?.value ?? option);
  };

  trackByHeader = (_index: number, column: any): string => {
    return String(column?.prop ?? column?.name ?? _index);
  };

  isDeleteColumn(column: any): boolean {
    return String(column?.prop ?? '') === 'delete';
  }

  openAuditHistory(row: any): void {
    const titleValue = String(row?.title ?? '').trim();
    const createdBy =
      this.data?.requestData?.created_by ||
      this.data?.created_by ||
      row?.created_by ||
      this.email ||
      '';
    const allowedAuditFields = (this.data?.headers || [])
      .map((header: any) => String(header?.prop ?? '').trim())
      .filter((key: string) => !!key);
    const fieldLabelMap = (this.data?.headers || []).reduce(
      (acc: Record<string, string>, header: any) => {
        const key = String(header?.prop ?? '').trim();
        const label = String(header?.name ?? '').trim();
        if (key && label) {
          acc[key] = label;
        }
        return acc;
      },
      {}
    );
    const auditRequestFilter = this.buildAuditHistoryPayload(titleValue);

    this.dialog.open(PopupTableComponent, {
        width: '1200px',
      height: '700px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: {
        title: 'Audit History',
        headers: [
          { key: 'field', label: 'Field', width: 220 },
          { key: 'oldValue', label: 'Old Value', width: 220 },
          { key: 'newValue', label: 'New Value', width: 220 },
          { key: 'updatedBy', label: 'Updated By', width: 200 },
          { key: 'updatedOn', label: 'Updated On', width: 180 },
        ],
        // Placeholder rows so popup works for all tables now.
        rows: [
          {
            field: 'Pending payload',
            oldValue: '-',
            newValue: '-',
            updatedBy: '-',
            updatedOn: '-',
          },
        ],
        apiUrl: this.data?.auditHistoryApiUrl || '/spriced/platform/getData',
        requestPayload: auditRequestFilter,
        useRawPayload: true,
        applyAuditFieldHeaderMapping: this.shouldApplyScopedAuditMapping(),
        allowedAuditFields,
        fieldLabelMap,
        sourceRow: row,
      },
    });
  }

  private shouldApplyScopedAuditMapping(): boolean {
    const routePath = String(window?.location?.pathname ?? '')
      .toLowerCase()
      .replace(/\/+$/, '');
    return new Set([
      '/ideas-hopper',
      '/sim-planning',
      '/aop-planning',
      '/aop-planning-2026',
      '/aop-planning-2027',
    ]).has(
      routePath
    );
  }

  private buildAuditHistoryPayload(titleValue: string): any {
    const routePath = String(window?.location?.pathname ?? '')
      .toLowerCase()
      .replace(/\/+$/, '');
    const scopedAuditHistoryRoutes = new Set([
      '/ideas-hopper',
      '/sim-planning',
      '/aop-planning',
      '/aop-planning-2026',
      '/aop-planning-2027',
    ]);

    if (scopedAuditHistoryRoutes.has(routePath)) {
      return {
        entityName: 'ecosystem',
        attributesList: ['*'],
        filterList: [
          {
            attributeName: 'title',
            operator: 'IN',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: [titleValue],
          },
        ],
      };
    }

    return {
      entityName: 'ecosystem',
      attributesList: ['*'],
      filterList: [
        {
          attributeName: 'title',
          operator: 'IN',
          logicalOperator: '',
          beginScope: '',
          endScope: '',
          attributeValue: [titleValue],
        },
      ],
    };
  }

  private getDropdownOptionKey(value: any): string {
    return `${typeof value}:${String(value)}`;
  }

  onSearchOptionSelection(event: any): void {
    if (event?.isUserInput && event?.source?.selected) {
      event.source.deselect();
    }
  }

  onDropdownOpenedChange(
    isOpen: boolean,
    rowIndex: number,
    columnProp: string,
    column: any
  ): void {
    if (isOpen || !this.shouldEnableDropdownSearch(column)) {
      return;
    }

    this.onDropdownSearchInput(rowIndex, columnProp, '');
  }

  hasUnmatchedValues(values: any, list: any[]): boolean {
    if (Array.isArray(values)) {
      return this.getUnmatchedValues(values, list).length > 0;
    } else {
      return !this.isBlank(values) && !this.isValueInList(values, list);
    }
  }
  //-----------------------------------------------------

  isCellInvalid(row: any, column: any) {
    // If neither highlightInvalid nor mandatory+highlightInvalid applies → valid
    if (!column.highlightInvalid) return false;

    const value = row[column.prop];

    // Mandatory blank (only if highlightInvalid=true)
    if (this.isColumnMandatoryForRow(column, row) && this.isBlank(value)) {
      return true;
    }

    // Dropdown logic
    if (column.type === 'dropdown') {
      if (column.isMultiSelect) {
        return this.hasUnmatchedValues(value, column.list);
      } else {
        return !this.isValueInList(value, column.list);
      }
    }
    return false;
  }

  isRowInvalid(row: any) {
    return this.data.headers.some((col: any) => {
      if (!col.highlightInvalid) return false;
      return this.isCellInvalid(row, col);
    });
  }

  private hasScoreErrorMessage(row: any): boolean {
    const message = row?.score_error_message;
    return !this.isBlank(message);
  }

  getHighlightRed(row: any, column: any): boolean {
    if (!column?.highlightNull) return false;

    const numericValue = Number(row?.[column.prop]);
    const isFiniteValue = Number.isFinite(numericValue);

    if (column.prop === 'final_score') {
      return !isFiniteValue || numericValue === 0 || this.hasScoreErrorMessage(row);
    }

    return !isFiniteValue;
  }

  getHighlightGreen(row: any, column: any): boolean {
    if (!column?.highlightNull) return false;

    const numericValue = Number(row?.[column.prop]);
    const isFiniteValue = Number.isFinite(numericValue);

    if (column.prop === 'final_score') {
      return isFiniteValue && numericValue !== 0 && !this.hasScoreErrorMessage(row);
    }

    return isFiniteValue;
  }

  toggleInvalidFilter() {
    this.showInvalidOnly = !this.showInvalidOnly;
    this.applyInvalidFilter();
  }
  applyInvalidFilter() {
    if (this.showInvalidOnly) {
      this.rows = this.cloneRows(
        this.originalRows.filter((row) => this.isRowInvalid(row))
      );
    } else {
      this.rows = this.cloneRows(this.originalRows); // reset
    }

    this.applyCurrentSort();
  }
}

